// Резак: три способа резать модель — отрезать часть по клику (плоскость встаёт туда, где часть
// встречается с моделью), разрезать плоскостью целиком, поделить под размер стола. Соединения:
// замок, ласточкин хвост, штифты. Тяжёлое — в worker.js; здесь интерфейс, просмотр и превью.
import { bedPicker, createStage, THREE, type Ctx, type Model, type Part, type Placed } from '@gen3d/core';
import { isBambu, toBambu3MF, type BObject } from '../../split-colors/src/bambu';
import { DEFAULT_OPTS, type Opts } from './opts';
import type { Plane, V3 } from './topo';
import type { BodyMsg, GridCut, Knife, Req, Res, SPt, Where } from './worker';

type Mode = 'part' | 'plane' | 'line' | 'surf' | 'bed' | 'brush';

const T = {
  uk: {
    sample: 'Відкрити з прикладом', sampleErr: 'Не вдалося завантажити приклад — перевірте зʼєднання.', file: 'Файл', pick: 'Обрати файл', drop: '3MF, STL або OBJ — або перетягніть сюди', again: 'Інший файл', local: 'Файл обробляється у вашому браузері й нікуди не надсилається.',
    loading: 'Читаємо файл…', counting: 'Рахуємо розрізи…',
    brushPaint: 'Фарба', brushErase: 'Гумка', brushR: 'Розмір', clearPaint: 'Очистити', toolRotate: 'Крутити', toolPaint: 'Фарбувати',
    lnOne: 'Поставте ще точку.', lnOpen: 'Точок: {n}. Лінія відкрита — деталь з одного боку.', lnClosed: 'Точок: {n}. Контур замкнений — деталь усередині.', lnUndo: 'Прибрати точку', sfLock: 'Пробка йде поперек зрізу й закінчується рівним торцем — ним деталь стає на стіл. Пробка йде від самого краю зрізу, стінки до торця — під кутом (до 45°), тож друкується без підтримок. У моделі — гніздо з зазором.', lnLock: 'Гребінь уздовж шва входить у паз моделі — деталь не зсувається. Вздовж шва він повторює його форму.',
    pinN: 'Штифтів: {n}', pinAuto: ' — розставлено автоматично', pinAdd: '+ Штифт', pinReset: 'Автоматично', pinHint: 'Тягніть штифт по зрізу. Натисніть на штифт — він прибереться.', pinPlace: 'Натисніть на зріз там, де потрібен штифт.',
    view: { front: 'Спереду', back: 'Ззаду', left: 'Зліва', right: 'Справа', top: 'Зверху', bottom: 'Знизу' }, viewL: 'Вид',
    painted: 'Зріз — по краю зафарбованого. Можна дофарбувати або підправити повзунками.', paintAll: 'Зафарбовано всю модель — зітріть гумкою те, що має залишитися.',
    mode: { part: 'Частину', plane: 'Площиною', bed: 'Під стіл', brush: 'Кисть', line: 'Лінією', surf: 'По поверхні' },
    modeT: { part: 'Відрізати частину', plane: 'Розрізати площиною', bed: 'Поділити під розмір столу', brush: 'Закрасити частину', line: 'Різати по лінії', surf: 'Різати по лінії на поверхні' },
    step1: { part: '① Що відрізати', plane: '① Де різати', bed: '① Розрізи', brush: '① Що відрізати', line: '① Лінія розрізу', surf: '① Лінія на поверхні' },
    hint: {
      part: 'Натисніть на частину моделі, яку треба надрукувати окремо, — вухо, лапу, хвіст.',
      plane: 'Розріз через усю модель. Оберіть вісь і рухайте зріз повзунком — або натисніть на модель там, де різати.',
      bed: 'Модель більша за стіл — різак сам поділить її на частини, що вміщуються, і поставить зʼєднання в кожен стик.',
      brush: 'Закрасьте частину, яку треба відрізати, — зріз пройде рівною площиною по краю зафарбованого.',
      line: 'Ставте точки кліком — між ними тягнеться плавна лінія, ніж пройде крізь модель по напрямку погляду. Натисніть на першу точку, щоб замкнути контур.',
      surf: 'Ставте точки просто на модель — лінія піде по її поверхні. Між точками модель можна крутити. Натисніть на першу точку, щоб замкнути контур.',
    },
    more: {
      part: 'Різак шукає місце, де частина зʼєднується з моделлю. Помаранчеве — те, що відріжеться. Повзунками можна зсунути й нахилити зріз.',
      plane: 'Помаранчеве — частина над зрізом: вона стане окремою деталлю, а пробка замка чи хвіст будуть на ній.',
      bed: 'Кожен розріз зсувається в межах запасу туди, де модель тонша. Розмір столу — в блоці «Площа друку» нижче. Хвіст тут не ставиться — замок або рівно.',
      brush: 'Для місць, де натискання не знаходить шийку. Фарбуйте всю частину, не лише край. На телефоні над моделлю перемикач «Крутити / Фарбувати», двома пальцями модель крутиться завжди. На компʼютері модель крутить права кнопка миші.',
      line: 'Спершу поставте вид кубиком зліва внизу, наприклад «Спереду»: ніж ріже так, як ви дивились у момент першої точки. Відкрита лінія ділить модель на дві частини — ведіть її за краї моделі. Замкнута вирізає те, що всередині.',
      surf: 'Замкнута лінія ділить модель на дві частини, помаранчева стане деталлю. Зріз — гладка поверхня, натягнута на лінію, як плівка на рамку; її видно жовтим. Зʼєднання — рівно або замок.',
    },
    finding: 'Шукаємо, де частина зʼєднується з моделлю…', found: 'Знайшли, де частина зʼєднується з моделлю.', notFound: 'Тут частина ні з чим не зʼєднується — зріз через точку натискання. Зсуньте його повзунком.',
    off: 'Зсув', tiltA: 'Нахил ↕', tiltB: 'Нахил ↔', pos: 'Висота', reset: 'Як було', clear: 'Зняти вибір', flip: 'Деталь з іншого боку', axis: 'Вісь',
    fits: 'Модель вміщується на стіл — різати не треба.', willBe: 'Вийде {n} част.: {d}.', perAxis: '{n} по {a}', bedIs: 'Стіл: {s} мм.',
    joint: '② Зʼєднання', jNone: 'Рівно', jLock: 'Замок', jTail: 'Хвіст',
    jHint: { none: 'Рівний зріз — деталі склеюються.', lock: 'Деталь входить у модель пробкою з рівним дном — ним вона лягає на стіл і при збиранні не зсувається.', tail: 'Ласточкин хвіст: деталь засувається збоку в паз і не виймається вгору.' },
    pins: 'Штифти', pNone: 'Без', pSep: 'Окремі', pBuilt: 'Вбудовані', pinsHint: 'Окремі — друкуються окремо, в обох частинах отвори. Вбудовані — ростуть із моделі, в деталі отвори.',
    fine: 'Тонко налаштувати', depth: 'Глибина замка', rim: 'Бортик', angle: 'Кут стінок', angleHint: '0° — стінки прямовисні, торець на весь зріз. Більше — торець менший, деталь легше входить. Понад 45° не буває: без підтримок не надрукується.', area: 'Площа дна', areaHint: '100 % — стінки прямовисні. Менше — стінки сходяться до дна (не крутіше 45°), деталь легше входить.', tailD: 'Глибина хвоста', tailW: 'Ширина хвоста', pinD: 'Діаметр', pinH: 'Глибина', tol: 'Зазор', tolShort: '0,15 щільно · 0,2 звичайно · 0,3 вільно',
    warn: { lockInset: 'Під зрізом модель вужча за пробку — пробку зроблено тоншою, по краю зрізу лишиться бортик.', lockThin: 'Замок не вміщується в зріз — буде рівний зріз. Зменште бортик або глибину.', tailThin: 'Хвіст не вміщується в зріз — буде рівний зріз. Зменште ширину чи глибину.', pinFit: 'Штифти не вміщуються в зріз.', pinOut: 'Штифт близько до краю — отвір може вийти назовні. Посуньте його.', noCut: 'Зріз не проходить через модель — зсуньте його.', sfNoSplit: 'Так розрізати не вийде: деталь тримається за модель ще в іншому місці (як нога — і за тіло, і за підставку), тому одна лінія її не відділить. Обведіть частину, яка кріпиться лише в одному місці.', sfTwist: 'Поверхня зрізу зачіпає модель (червоні точки) — поставте точки рівніше або частіше.', sfPath: 'Між сусідніми точками не вдалося провести лінію — поставте їх ближче.', sfCross: 'Лінія перетинає сама себе — приберіть або пересуньте точку.' },
    pvTime: 'Превʼю: {t}', estIs: 'розріз ≈ {t}',
    place: '③ Деталь на столі', plCut: 'Зрізом донизу', plModel: 'Як у моделі',
    result: '④ Деталі', vol: 'см³', mm: 'мм', s: 'с', min: 'хв',
    cut: { part: 'Відрізати', plane: 'Розрізати', bed: 'Поділити на {n}', brush: 'Відрізати', line: 'Розрізати', surf: 'Розрізати' }, cutting: 'Ріжемо', undo: 'Скасувати розріз',
    sfOpen: 'Точок: {n}. Замкніть контур — натисніть на першу точку.', sfClosed: 'Точок: {n}. Контур замкнений: помаранчеве — деталь, жовте — зріз.', ghost: 'Прозора модель', solid: 'Звичайна модель',
    kind: { body: 'модель', piece: 'деталь', pin: 'штифт' },
    done: 'Готово за {t}. Деталі лежать праворуч — можна різати далі або завантажувати.',
    dlWait: 'Щоб завантажити, спершу розріжте модель.',
    err: {
      BAD_FILE: 'Файл не вдалося прочитати. Підходять 3MF, STL та OBJ.', EMPTY: 'У файлі немає моделі.', NO_FILE: 'Спершу оберіть файл.',
      NOT_MANIFOLD: 'Сітка моделі має дірки — її не вдалося закрити. Спробуйте «Виправити модель» у слайсері.',
      NO_CUT: 'Зріз тут не проходить через модель — зсуньте його.', FAILED: 'Не вдалося розрізати. Спробуйте зсунути зріз.',
      MEMORY: 'Браузеру не вистачило памʼяті. Спростіть модель у слайсері (Simplify model).',
      TOO_BIG: 'Модель завелика: {n} млн трикутників, можна до 3 млн. Спростіть її в слайсері (Simplify model).',
      TOO_MANY: 'Вийшло б {n} частин — забагато. Візьміть більший стіл або зменште модель.',
      SF_NOSPLIT: 'Так розрізати не вийде: деталь тримається за модель ще в іншому місці (як нога — і за тіло, і за підставку), тому одна лінія її не відділить. Обведіть частину, яка кріпиться лише в одному місці.', SF_TWIST: 'Поверхня зрізу зачіпає модель — поставте точки рівніше або частіше.',
      SF_CROSS: 'Лінія перетинає сама себе — приберіть або пересуньте точку.', SF_PATH: 'Між сусідніми точками не вдалося провести лінію — поставте їх ближче.',
    },
  },
  ru: {
    sample: 'Открыть с примером', sampleErr: 'Не удалось загрузить пример — проверьте соединение.', file: 'Файл', pick: 'Выбрать файл', drop: '3MF, STL или OBJ — или перетащите сюда', again: 'Другой файл', local: 'Файл обрабатывается в вашем браузере и никуда не отправляется.',
    loading: 'Читаем файл…', counting: 'Считаем разрезы…',
    brushPaint: 'Кисть', brushErase: 'Ластик', brushR: 'Размер', clearPaint: 'Очистить', toolRotate: 'Крутить', toolPaint: 'Красить',
    lnOne: 'Поставьте ещё точку.', lnOpen: 'Точек: {n}. Линия открыта — деталь с одной стороны.', lnClosed: 'Точек: {n}. Контур замкнут — деталь внутри.', lnUndo: 'Убрать точку', sfLock: 'Пробка идёт поперёк среза и заканчивается ровным торцом — им деталь встаёт на стол. Пробка идёт от самого края среза, стенки к торцу — под углом (до 45°), так что печатается без поддержек. В модели — гнездо с зазором.', lnLock: 'Гребень вдоль шва входит в паз модели — деталь не сдвигается. Вдоль шва он повторяет его форму.',
    pinN: 'Штифтов: {n}', pinAuto: ' — расставлены автоматически', pinAdd: '+ Штифт', pinReset: 'Автоматически', pinHint: 'Тяните штифт по срезу. Нажмите на штифт — он уберётся.', pinPlace: 'Нажмите на срез там, где нужен штифт.',
    view: { front: 'Спереди', back: 'Сзади', left: 'Слева', right: 'Справа', top: 'Сверху', bottom: 'Снизу' }, viewL: 'Вид',
    painted: 'Срез — по краю закраски. Можно докрасить или поправить ползунками.', paintAll: 'Закрашена вся модель — сотрите ластиком то, что должно остаться.',
    mode: { part: 'Часть', plane: 'Плоскостью', bed: 'Под стол', brush: 'Кисть', line: 'Линией', surf: 'По поверхности' },
    modeT: { part: 'Отрезать часть', plane: 'Разрезать плоскостью', bed: 'Поделить под размер стола', brush: 'Закрасить часть', line: 'Резать по линии', surf: 'Резать по линии на поверхности' },
    step1: { part: '① Что отрезать', plane: '① Где резать', bed: '① Разрезы', brush: '① Что отрезать', line: '① Линия разреза', surf: '① Линия на поверхности' },
    hint: {
      part: 'Нажмите на часть модели, которую нужно напечатать отдельно, — ухо, лапу, хвост.',
      plane: 'Разрез через всю модель. Выберите ось и двигайте срез ползунком — или нажмите на модель там, где резать.',
      bed: 'Модель больше стола — резак сам поделит её на части, которые помещаются, и поставит соединения в каждый стык.',
      brush: 'Закрасьте часть, которую нужно отрезать, — срез пройдёт ровной плоскостью по краю закраски.',
      line: 'Ставьте точки кликом — между ними тянется плавная линия, нож пройдёт сквозь модель по направлению взгляда. Нажмите на первую точку, чтобы замкнуть контур.',
      surf: 'Ставьте точки прямо на модель — линия пойдёт по её поверхности. Между точками модель можно крутить. Нажмите на первую точку, чтобы замкнуть контур.',
    },
    more: {
      part: 'Резак ищет место, где часть соединяется с моделью. Оранжевое — то, что отрежется. Ползунками можно сдвинуть и наклонить срез.',
      plane: 'Оранжевое — часть над срезом: она станет отдельной деталью, а пробка замка или хвост будут на ней.',
      bed: 'Каждый разрез сдвигается в пределах запаса туда, где модель тоньше. Размер стола — в блоке «Площадь печати» ниже. Хвост здесь не ставится — замок или ровно.',
      brush: 'Для мест, где нажатие не находит шейку. Красьте всю часть, не только край. На телефоне над моделью переключатель «Крутить / Красить», двумя пальцами модель крутится всегда. На компьютере модель крутит правая кнопка мыши.',
      line: 'Сначала поставьте вид кубиком слева внизу, например «Спереди»: нож режет так, как вы смотрели в момент первой точки. Открытая линия делит модель на две части — ведите её за края модели. Замкнутая вырезает то, что внутри.',
      surf: 'Замкнутая линия делит модель на две части, оранжевая станет деталью. Срез — гладкая поверхность, натянутая на линию, как плёнка на рамку; она видна жёлтым. Соединение — ровно или замок.',
    },
    finding: 'Ищем, где часть соединяется с моделью…', found: 'Нашли, где часть соединяется с моделью.', notFound: 'Здесь часть ни с чем не соединяется — срез через точку нажатия. Сдвиньте его ползунком.',
    off: 'Сдвиг', tiltA: 'Наклон ↕', tiltB: 'Наклон ↔', pos: 'Высота', reset: 'Как было', clear: 'Снять выбор', flip: 'Деталь с другой стороны', axis: 'Ось',
    fits: 'Модель помещается на стол — резать не нужно.', willBe: 'Получится {n} част.: {d}.', perAxis: '{n} по {a}', bedIs: 'Стол: {s} мм.',
    joint: '② Соединение', jNone: 'Ровно', jLock: 'Замок', jTail: 'Хвост',
    jHint: { none: 'Ровный срез — детали склеиваются.', lock: 'Деталь входит в модель пробкой с ровным дном — им она ложится на стол и при сборке не сдвигается.', tail: 'Ласточкин хвост: деталь задвигается сбоку в паз и не вынимается вверх.' },
    pins: 'Штифты', pNone: 'Без', pSep: 'Отдельные', pBuilt: 'Встроенные', pinsHint: 'Отдельные — печатаются отдельно, в обеих частях отверстия. Встроенные — растут из модели, в детали отверстия.',
    fine: 'Тонкая настройка', depth: 'Глубина замка', rim: 'Бортик', angle: 'Угол стенок', angleHint: '0° — стенки отвесные, торец во весь срез. Больше — торец меньше, деталь легче входит. Больше 45° нельзя: без поддержек не напечатается.', area: 'Площадь дна', areaHint: '100 % — стенки отвесные. Меньше — стенки сходятся к дну (не круче 45°), деталь легче входит.', tailD: 'Глубина хвоста', tailW: 'Ширина хвоста', pinD: 'Диаметр', pinH: 'Глубина', tol: 'Зазор', tolShort: '0,15 плотно · 0,2 обычно · 0,3 свободно',
    warn: { lockInset: 'Под срезом модель уже пробки — пробка сделана тоньше, по краю среза останется бортик.', lockThin: 'Замок не помещается в срез — будет ровный срез. Уменьшите бортик или глубину.', tailThin: 'Хвост не помещается в срез — будет ровный срез. Уменьшите ширину или глубину.', pinFit: 'Штифты не помещаются в срез.', pinOut: 'Штифт близко к краю — отверстие может выйти наружу. Сдвиньте его.', noCut: 'Срез не проходит через модель — сдвиньте его.', sfNoSplit: 'Так разрезать не получится: деталь держится за модель ещё в другом месте (как нога — и за тело, и за подставку), поэтому одна линия её не отделит. Обведите часть, которая крепится только в одном месте.', sfTwist: 'Поверхность среза задевает модель (красные точки) — поставьте точки ровнее или чаще.', sfPath: 'Между соседними точками не удалось провести линию — поставьте их ближе.', sfCross: 'Линия пересекает сама себя — уберите или передвиньте точку.' },
    pvTime: 'Превью: {t}', estIs: 'разрез ≈ {t}',
    place: '③ Деталь на столе', plCut: 'Срезом вниз', plModel: 'Как в модели',
    result: '④ Детали', vol: 'см³', mm: 'мм', s: 'с', min: 'мин',
    cut: { part: 'Отрезать', plane: 'Разрезать', bed: 'Поделить на {n}', brush: 'Отрезать', line: 'Разрезать', surf: 'Разрезать' }, cutting: 'Режем', undo: 'Отменить разрез',
    sfOpen: 'Точек: {n}. Замкните контур — нажмите на первую точку.', sfClosed: 'Точек: {n}. Контур замкнут: оранжевое — деталь, жёлтое — срез.', ghost: 'Прозрачная модель', solid: 'Обычная модель',
    kind: { body: 'модель', piece: 'деталь', pin: 'штифт' },
    done: 'Готово за {t}. Детали лежат справа — можно резать дальше или скачивать.',
    dlWait: 'Чтобы скачать, сначала разрежьте модель.',
    err: {
      BAD_FILE: 'Файл не удалось прочитать. Подходят 3MF, STL и OBJ.', EMPTY: 'В файле нет модели.', NO_FILE: 'Сначала выберите файл.',
      NOT_MANIFOLD: 'Сетка модели с дырами — её не удалось закрыть. Попробуйте «Исправить модель» в слайсере.',
      NO_CUT: 'Срез здесь не проходит через модель — сдвиньте его.', FAILED: 'Не удалось разрезать. Попробуйте сдвинуть срез.',
      MEMORY: 'Браузеру не хватило памяти. Упростите модель в слайсере (Simplify model).',
      TOO_BIG: 'Модель слишком большая: {n} млн треугольников, можно до 3 млн. Упростите её в слайсере (Simplify model).',
      TOO_MANY: 'Получилось бы {n} частей — слишком много. Возьмите стол побольше или уменьшите модель.',
      SF_NOSPLIT: 'Так разрезать не получится: деталь держится за модель ещё в другом месте (как нога — и за тело, и за подставку), поэтому одна линия её не отделит. Обведите часть, которая крепится только в одном месте.', SF_TWIST: 'Поверхность среза задевает модель — поставьте точки ровнее или чаще.',
      SF_CROSS: 'Линия пересекает сама себя — уберите или передвиньте точку.', SF_PATH: 'Между соседними точками не удалось провести линию — поставьте их ближе.',
    },
  },
  en: {
    sample: 'Open with an example', sampleErr: 'Could not load the example — check your connection.', file: 'File', pick: 'Choose file', drop: '3MF, STL or OBJ — or drop it here', again: 'Another file', local: 'The file is processed in your browser and is not uploaded anywhere.',
    loading: 'Reading the file…', counting: 'Planning the cuts…',
    brushPaint: 'Paint', brushErase: 'Eraser', brushR: 'Size', clearPaint: 'Clear', toolRotate: 'Rotate', toolPaint: 'Paint',
    lnOne: 'Place one more point.', lnOpen: '{n} points. The line is open — the part is on one side.', lnClosed: '{n} points. The outline is closed — the part is inside.', lnUndo: 'Remove point', sfLock: 'The plug goes across the cut and ends in a flat face — the part stands on the bed on it. The plug starts right at the cut edge and its walls slope to the face (up to 45°), so it prints without supports. The model gets a socket with clearance.', lnLock: 'A ridge along the seam fits a groove in the model — the part does not slip. It follows the shape of the seam.',
    pinN: 'Pins: {n}', pinAuto: ' — placed automatically', pinAdd: '+ Pin', pinReset: 'Automatic', pinHint: 'Drag a pin along the cut. Tap a pin to remove it.', pinPlace: 'Tap the cut where you want a pin.',
    view: { front: 'Front', back: 'Back', left: 'Left', right: 'Right', top: 'Top', bottom: 'Bottom' }, viewL: 'View',
    painted: 'The cut follows the edge of the paint. Paint more or adjust with the sliders.', paintAll: 'The whole model is painted — erase what should stay.',
    mode: { part: 'Part', plane: 'Plane', bed: 'To bed', brush: 'Brush', line: 'Line', surf: 'Surface' },
    modeT: { part: 'Cut off a part', plane: 'Cut with a plane', bed: 'Split to fit the bed', brush: 'Paint the part', line: 'Cut along a line', surf: 'Cut along a line on the surface' },
    step1: { part: '① What to cut off', plane: '① Where to cut', bed: '① Cuts', brush: '① What to cut off', line: '① Cut line', surf: '① Line on the surface' },
    hint: {
      part: 'Click the part of the model you want to print separately — an ear, a paw, a tail.',
      plane: 'A cut through the whole model. Pick an axis and move the cut with the slider — or click the model where to cut.',
      bed: 'The model is bigger than the bed — the cutter splits it into parts that fit and adds a joint at every seam.',
      brush: 'Paint the part to cut off — the cut goes as a flat plane along the edge of the paint.',
      line: 'Click to place points — a smooth line runs through them, and the knife goes through the model along your view. Click the first point to close the outline.',
      surf: 'Place points right on the model — the line follows its surface. You can rotate the model between points. Click the first point to close the outline.',
    },
    more: {
      part: 'The cutter looks for where the part joins the model. Orange is what will be cut off. Use the sliders to move and tilt the cut.',
      plane: 'Orange is the part above the cut: it becomes a separate part and carries the plug or the dovetail.',
      bed: 'Each cut moves within the spare room to where the model is thinner. The bed size is in “Print area” below. No dovetail here — plug or flat.',
      brush: 'For places where a click does not find the neck. Paint the whole part, not just its edge. On a phone, use the “Rotate / Paint” switch above the model; two fingers always rotate. On a computer, the right mouse button rotates.',
      line: 'First set the view with the cube at the bottom left, e.g. “Front”: the knife cuts the way you looked when placing the first point. An open line splits the model in two — run it past the model edges. A closed outline cuts out what is inside.',
      surf: 'A closed line splits the model in two; the orange side becomes the part. The cut is a smooth surface stretched over the line like a film on a frame; it shows in yellow. Joint — flat or a plug.',
    },
    finding: 'Finding where the part joins the model…', found: 'Found where the part joins the model.', notFound: 'This part does not join anything here — the cut goes through the clicked point. Move it with the slider.',
    off: 'Offset', tiltA: 'Tilt ↕', tiltB: 'Tilt ↔', pos: 'Height', reset: 'Reset', clear: 'Clear', flip: 'Part on the other side', axis: 'Axis',
    fits: 'The model fits the bed — no cutting needed.', willBe: '{n} parts: {d}.', perAxis: '{n} along {a}', bedIs: 'Bed: {s} mm.',
    joint: '② Joint', jNone: 'Flat', jLock: 'Plug', jTail: 'Dovetail',
    jHint: { none: 'A flat cut — glue the parts.', lock: 'The part goes into the model with a flat-bottomed plug: it lies on the bed on that bottom and does not slip when assembled.', tail: 'Dovetail: the part slides into a groove from the side and cannot lift out.' },
    pins: 'Pins', pNone: 'None', pSep: 'Separate', pBuilt: 'Built-in', pinsHint: 'Separate pins print on their own, with holes in both parts. Built-in pins grow from the model, with holes in the part.',
    fine: 'Fine-tune', depth: 'Plug depth', rim: 'Rim', angle: 'Wall angle', angleHint: '0° — straight walls, the face is as big as the cut. More — a smaller face, the part goes in more easily. No more than 45°, or it needs supports.', area: 'Bottom area', areaHint: '100% — straight walls. Less — the walls taper to the bottom (no steeper than 45°), so the part goes in more easily.', tailD: 'Dovetail depth', tailW: 'Dovetail width', pinD: 'Diameter', pinH: 'Depth', tol: 'Clearance', tolShort: '0.15 tight · 0.2 normal · 0.3 loose',
    warn: { lockInset: 'The model is narrower than the plug below the cut — the plug is made thinner, leaving a rim along the cut edge.', lockThin: 'The plug does not fit the cut — it will be flat. Reduce the rim or depth.', tailThin: 'The dovetail does not fit the cut — it will be flat. Reduce its width or depth.', pinFit: 'The pins do not fit the cut.', pinOut: 'A pin is close to the edge — its hole may break through. Move it.', noCut: 'The cut misses the model — move it.', sfNoSplit: 'This cut will not work: the part is attached to the model somewhere else too (like a leg — to both the body and the base), so one line cannot separate it. Outline a part that is attached in one place only.', sfTwist: 'The cut surface hits the model (red dots) — place the points more evenly or closer together.', sfPath: 'Could not draw the line between two points — place them closer.', sfCross: 'The line crosses itself — remove or move a point.' },
    pvTime: 'Preview: {t}', estIs: 'cut ≈ {t}',
    place: '③ Part on the bed', plCut: 'Cut side down', plModel: 'As in the model',
    result: '④ Parts', vol: 'cm³', mm: 'mm', s: 's', min: 'min',
    cut: { part: 'Cut off', plane: 'Cut', bed: 'Split into {n}', brush: 'Cut off', line: 'Cut', surf: 'Cut' }, cutting: 'Cutting', undo: 'Undo cut',
    sfOpen: '{n} points. Close the outline — click the first point.', sfClosed: '{n} points. The outline is closed: orange is the part, yellow is the cut.', ghost: 'See-through model', solid: 'Solid model',
    kind: { body: 'model', piece: 'part', pin: 'pin' },
    done: 'Done in {t}. The parts lie to the right — cut more or download.',
    dlWait: 'Cut the model first to download.',
    err: {
      BAD_FILE: 'Could not read the file. Use 3MF, STL or OBJ.', EMPTY: 'The file has no model.', NO_FILE: 'Choose a file first.',
      NOT_MANIFOLD: 'The model mesh has holes that could not be closed. Try “Fix model” in your slicer.',
      NO_CUT: 'The cut does not pass through the model here — move it.', FAILED: 'Could not cut. Try moving the cut.',
      MEMORY: 'The browser ran out of memory. Simplify the model in your slicer (Simplify model).',
      TOO_BIG: 'The model is too large: {n} M triangles, the limit is 3 M. Simplify it in your slicer (Simplify model).',
      TOO_MANY: 'That would be {n} parts — too many. Use a bigger bed or scale the model down.',
      SF_NOSPLIT: 'This cut will not work: the part is attached to the model somewhere else too (like a leg — to both the body and the base), so one line cannot separate it. Outline a part that is attached in one place only.', SF_TWIST: 'The cut surface hits the model — place the points more evenly or closer together.',
      SF_CROSS: 'The line crosses itself — remove or move a point.', SF_PATH: 'Could not draw the line between two points — place them closer.',
    },
  },
};

/** Пределы настроек: min, max, шаг. */
const LIM = { depth: [2, 20, 0.5], rim: [0.5, 5, 0.1], area: [1, 100, 1], angle: [0, 45, 1], tailD: [2, 15, 0.5], tailW: [15, 60, 1], pinD: [2, 10, 0.5], pinH: [2, 15, 0.5], tol: [0, 0.6, 0.05] } as const;
const BR = [2, 40, 1] as const;
const ADJ = { off: [-30, 30, 0.5], a: [-45, 45, 1], b: [-45, 45, 1] } as const;
/** Секунд на треугольник разреза — начальная оценка (заяц, 1,3 млн, ноутбук: 8 с); дальше подстраивается. */
const K0 = 6e-6;
const AX = ['X', 'Y', 'Z'];
const CELL_COL = ['#F97316', '#3B82F6', '#22C55E', '#EAB308', '#EC4899', '#14B8A6', '#A855F7', '#EF4444'];

const CSS = `
.ct-drop{display:flex;flex-direction:column;align-items:center;gap:6px;padding:18px 12px;border:1.5px dashed var(--line);border-radius:var(--radius-sm);text-align:center;cursor:pointer}
.ct-drop.over{border-color:var(--accent);background:color-mix(in srgb,var(--accent) 7%,transparent)}
.ct-drop .btn{min-height:44px}
.ct-sample{width:100%;min-height:44px;margin-top:8px}
.ct-sample[hidden]{display:none}
.ct-drop small{color:var(--muted);font-size:13px}
.ct-drop.got{flex-direction:row-reverse;justify-content:space-between;gap:10px;padding:6px 6px 6px 10px;text-align:left}
.ct-drop.got small{font:500 13px/1.3 var(--font-mono);color:var(--text);word-break:break-all}
.ct-note{font-size:13px;line-height:1.35;margin:0 0 6px;padding:8px 10px;border-radius:var(--radius-sm);border-left:3px solid var(--accent);background:color-mix(in srgb,var(--accent) 8%,transparent)}
.ct-note[hidden],.ct-p [hidden]{display:none!important}
.ct-note.warn{border-left-color:var(--bad,#d33);background:color-mix(in srgb,var(--bad,#d33) 8%,transparent)}
.ct-tiles{display:grid;grid-template-columns:repeat(var(--n,2),1fr);gap:6px;margin:2px 0 6px}
.ct-tile{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;min-height:44px;padding:4px 4px;border:1.5px solid var(--line);border-radius:var(--radius-sm);background:none;color:var(--text);font:inherit;font-size:13px;line-height:1.15;text-align:center;cursor:pointer}
.ct-tile svg{width:34px;height:22px}
.ct-tile[aria-pressed=true]{border-color:var(--accent);background:color-mix(in srgb,var(--accent) 9%,transparent)}
.ct-lbl{font-size:12px;color:var(--muted);margin:6px 0 2px}
.ct-row{display:flex;align-items:center;gap:6px;margin:0}
.ct-row>span:first-child{flex:none;width:84px;font-size:13px;line-height:1.15}
.ct-row input[type=range]{flex:1;min-width:0;accent-color:var(--accent);height:44px}
.ct-sub{font-size:11px;color:var(--muted);margin:0 0 4px}
.ct-time{font:400 12px/1.3 var(--font-mono);color:var(--muted);margin:2px 0 4px}
.ct-time:empty{display:none}
.ct-fine{margin:4px 0 0}
.ct-fine>summary{display:flex;align-items:center;min-height:44px;font-size:13px;color:var(--accent-text,var(--accent));cursor:pointer;list-style:none}
.ct-fine>summary::-webkit-details-marker{display:none}
.ct-fine>summary::before{content:"▸";margin-right:6px;transition:transform .2s}
.ct-fine[open]>summary::before{transform:rotate(90deg)}
.ct-links{display:flex;gap:14px;flex-wrap:wrap}
.ct-pinrow{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:0 14px;margin-top:6px}.ct-pinrow>span:first-child{font-size:13px}.ct-pinrow .ct-sub{flex-basis:100%;margin-top:2px}
.ct-link{min-height:44px;padding:0 2px;border:0;background:none;color:var(--accent-text,var(--accent));font:inherit;font-size:14px;cursor:pointer;text-decoration:underline}
.ct-body{display:flex;align-items:center;gap:8px;min-height:36px;font-size:13px}
.ct-body i{width:12px;height:12px;border-radius:3px;flex:none}
.ct-body span{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ct-body small{color:var(--muted);font:400 12px/1 var(--font-mono);white-space:nowrap}
.ct-msg{font-size:14px;margin:6px 0 0}
.ct-msg:empty{display:none}
.ct-msg.bad{color:var(--bad)}
.ct-bar{height:6px;border-radius:3px;background:var(--surface-2);overflow:hidden;margin-top:8px}
.ct-bar i{display:block;height:100%;width:0;background:var(--accent);transition:width .3s}
.ct-modes{position:absolute;top:10px;left:50%;transform:translateX(-50%);z-index:4;display:flex;gap:2px;max-width:calc(100% - 16px);padding:3px;border-radius:999px;background:var(--surface);border:1px solid var(--line);box-shadow:0 6px 20px rgb(0 0 0/.18);overflow-x:auto;scrollbar-width:none}
.ct-modes::-webkit-scrollbar{display:none}
.ct-modes button{display:flex;align-items:center;justify-content:center;gap:6px;flex:none;min-height:44px;min-width:44px;padding:0 14px;border:0;border-radius:999px;background:none;color:var(--text);font:500 14px/1 var(--font-body,inherit);cursor:pointer;white-space:nowrap}
.ct-modes button svg{width:20px;height:20px;flex:none}
.ct-modes button[aria-selected=true]{background:var(--accent);color:var(--on-accent,#fff)}
.ct-modes button:disabled{opacity:.45;cursor:default}
.ct-modes button small{font-size:10px;opacity:.8}
.ct-go{position:absolute;left:50%;bottom:14px;transform:translateX(-50%);z-index:3;min-height:44px;padding:0 22px;box-shadow:0 6px 20px rgb(0 0 0/.25);white-space:nowrap}
.ct-go[hidden]{display:none}
.ct-status{position:absolute;top:66px;left:50%;transform:translateX(-50%);z-index:3;display:flex;align-items:center;gap:8px;max-width:calc(100% - 28px);padding:8px 14px;border-radius:999px;background:var(--surface);border:1px solid var(--line);box-shadow:0 6px 20px rgb(0 0 0/.18);font-size:13px;white-space:nowrap;pointer-events:none}
.ct-status[hidden]{display:none}
.ct-status i{flex:none;width:14px;height:14px;border-radius:50%;border:2px solid var(--line);border-top-color:var(--accent);animation:ct-spin .8s linear infinite}
.ct-status span{overflow:hidden;text-overflow:ellipsis}
.ct-tool{position:absolute;right:10px;top:66px;z-index:4;display:flex;gap:2px;padding:3px;border-radius:999px;background:var(--surface);border:1px solid var(--line);box-shadow:0 6px 20px rgb(0 0 0/.18)}
.ct-tool[hidden]{display:none}
.ct-tool button{display:flex;align-items:center;gap:6px;min-height:44px;min-width:44px;justify-content:center;padding:0 12px;border:0;border-radius:999px;background:none;color:var(--text);font:500 13px/1 var(--font-body,inherit);cursor:pointer}
.ct-tool button[aria-pressed=true]{background:var(--accent);color:var(--on-accent,#fff)}
.ct-tool svg{width:18px;height:18px;flex:none}
.ct-paint canvas{cursor:crosshair;touch-action:none}
@keyframes ct-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.ct-status i{animation:none}}
@media (max-width:860px){.ct-go{bottom:50px}}
@media (max-width:420px){.ct-tool span{display:none}.ct-tool button{padding:0}.ct-modes button{padding:0 9px;gap:4px;font-size:13px}.ct-modes button svg{width:18px;height:18px}.ct-modes button small,.ct-modes button:not([aria-selected=true]) span{display:none}}
@media (pointer:fine){.ct-tile{min-height:36px}.ct-link,.ct-fine>summary{min-height:32px}.ct-row input[type=range]{height:24px}.ct-modes button{min-height:36px}.ct-tool button{min-height:36px;min-width:36px}}
`;

// Значки: штриховка — модель, контур — отрезаемая деталь
const H = '<defs><pattern id="ct-h" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="4" stroke="currentColor" stroke-width="1.2"/></pattern></defs>';
const svg = (body: string) => `<svg viewBox="0 0 60 40" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true">${H}${body}</svg>`;
const ICON = {
  none: svg('<path d="M4 36H56V20H4Z" fill="url(#ct-h)"/><path d="M4 16H56V4H4Z"/>'),
  lock: svg('<path d="M4 36H56V20H42V30H18V20H4Z" fill="url(#ct-h)"/><path d="M4 16H56V4H4ZM20 16V27H40V16"/>'),
  tail: svg('<path d="M4 36H56V20H40L44 31H16L20 20H4Z" fill="url(#ct-h)"/><path d="M4 16H56V4H4ZM22 16L18 28H42L38 16"/>'),
  pNone: svg('<path d="M4 36H56V22H4Z" fill="url(#ct-h)"/><path d="M4 18H56V4H4Z"/>'),
  pSep: svg('<path d="M4 36H56V22H4Z" fill="url(#ct-h)"/><path d="M4 18H56V4H4Z"/><rect x="26" y="10" width="8" height="20" rx="1.5"/>'),
  pBuilt: svg('<path d="M4 36H56V22H34V10H26V22H4Z" fill="url(#ct-h)"/><path d="M4 18H56V4H4Z"/>'),
  plCut: svg('<path d="M10 34H50" /><path d="M18 34V18Q30 2 42 18V34" />'),
  plModel: svg('<path d="M10 34H50" stroke-dasharray="3 3"/><path d="M14 30L40 8Q52 10 46 22L22 34Z"/>'),
};
const MODE_ICON = {
  part: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="9" cy="15" r="6"/><path d="M13 11l5-7a1.6 1.6 0 0 1 2.8 1.5L16 13"/><path d="M11 8l7 6" stroke-dasharray="2 2"/></svg>',
  plane: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M8 3h8v18H8z"/><path d="M3 12h18" stroke-dasharray="2.5 2"/></svg>',
  bed: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 3v18M3 12h18" stroke-dasharray="2.5 2"/></svg>',
  line: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M3 18c4-1 4-9 9-9s5 6 9 4"/><circle cx="3" cy="18" r="1.8" fill="currentColor"/><circle cx="12" cy="9" r="1.8" fill="currentColor"/><circle cx="21" cy="13" r="1.8" fill="currentColor"/></svg>',
  brush: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M4 20c3 0 4-2 4-4a2 2 0 0 1 4 0c0 3-3 4-8 4Z"/><path d="M11 13 20 4"/></svg>',
  surf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M5 8c1-4 6-5 10-4s6 5 5 9-4 7-9 7-7-3-7-6 0-4 1-6Z"/><path d="M3.5 12c4 3 12 3 17-1" stroke-dasharray="2.5 2"/></svg>',
};

type Store = { load(): unknown; save(v: unknown): void };
const css = (v: string, d: string) => getComputedStyle(document.documentElement).getPropertyValue(v).trim() || d;
const COL: Record<BodyMsg['kind'], [string, string]> = { body: ['--model', '#8E84F7'], piece: ['--model-2', '#F6F4FF'], pin: ['--model-2', '#F6F4FF'] };
const CUT = '#F97316';

const dot = (a: V3, b: V3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const unit = (a: V3): V3 => { const l = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
const cross = (a: V3, b: V3): V3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
/** Нормаль плоскости по разбросу точек: собственный вектор наименьшего значения (Якоби, 3×3). */
function smallestEig(m: number[][]): V3 {
  const a = m.map((r) => [...r]), v = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
  for (let it = 0; it < 50; it++) {
    let p = 0, q = 1;
    if (Math.abs(a[0][2]) > Math.abs(a[p][q])) { p = 0; q = 2; }
    if (Math.abs(a[1][2]) > Math.abs(a[p][q])) { p = 1; q = 2; }
    if (Math.abs(a[p][q]) < 1e-12) break;
    const th = (a[q][q] - a[p][p]) / (2 * a[p][q]);
    const tt = (th >= 0 ? 1 : -1) / (Math.abs(th) + Math.sqrt(th * th + 1)), c = 1 / Math.sqrt(tt * tt + 1), s = tt * c;
    for (let k = 0; k < 3; k++) { const x = a[k][p], y = a[k][q]; a[k][p] = c * x - s * y; a[k][q] = s * x + c * y; }
    for (let k = 0; k < 3; k++) { const x = a[p][k], y = a[q][k]; a[p][k] = c * x - s * y; a[q][k] = s * x + c * y; }
    for (let k = 0; k < 3; k++) { const x = v[k][p], y = v[k][q]; v[k][p] = c * x - s * y; v[k][q] = s * x + c * y; }
  }
  let i = 0;
  for (let k = 1; k < 3; k++) if (a[k][k] < a[i][i]) i = k;
  return unit([v[0][i], v[1][i], v[2][i]]);
}
/** Наклон нормали n0 на a и b градусов (вокруг осей плоскости). */
const tilt = (n0: V3, a: number, b: number): V3 => {
  const u = unit(cross(Math.abs(n0[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0], n0)), v = cross(n0, u);
  const ta = Math.tan((a * Math.PI) / 180), tb = Math.tan((b * Math.PI) / 180);
  return unit([n0[0] + v[0] * ta + u[0] * tb, n0[1] + v[1] * ta + u[1] * tb, n0[2] + v[2] * ta + u[2] * tb]);
};

export function mount(root: HTMLElement, { lang, store, sample }: Ctx & { store?: Store }) {
  const t = T[lang] ?? T.uk;
  const opts: Opts = { ...DEFAULT_OPTS };
  let mode: Mode = 'plane';
  let brushR = 8;
  try {
    const v = store?.load() as { opts?: Record<string, unknown>; mode?: unknown; brushR?: unknown } | null;
    const o = v?.opts;
    // «Часть» и «Кисть» убраны из вкладок (20.10.2026) — сохранённые с ними настройки открываются на «Плоскостью»
    if (v?.mode === 'plane' || v?.mode === 'line' || v?.mode === 'surf' || v?.mode === 'bed') mode = v.mode;
    if (typeof v?.brushR === 'number' && isFinite(v.brushR)) brushR = Math.min(BR[1], Math.max(BR[0], v.brushR));
    if (o) {
      if (o.joint === 'none' || o.joint === 'lock' || o.joint === 'tail') opts.joint = o.joint;
      else if (typeof o.lock === 'boolean') opts.joint = o.lock ? 'lock' : 'none'; // прежний флажок «замок»
      if (o.pins === 'none' || o.pins === 'sep' || o.pins === 'built') opts.pins = o.pins;
      if (o.place === 'cut' || o.place === 'model') opts.place = o.place;
      for (const k of Object.keys(LIM) as (keyof typeof LIM)[]) { const x = o[k]; if (typeof x === 'number' && isFinite(x)) opts[k] = Math.min(LIM[k][1], Math.max(LIM[k][0], x)); }
    }
  } catch { /* типовые */ }
  const save = () => { try { store?.save({ opts, mode, brushR }); } catch { /* сховище недоступне */ } };
  /** Скорость разреза на этом компьютере: секунд на треугольник. */
  let K = K0;
  try { const k = +(localStorage.getItem('gen3d:cutter:k') ?? ''); if (k > 1e-8 && k < 1e-3) K = k; } catch { /* нет хранилища */ }

  document.head.insertAdjacentHTML('beforeend', `<style>${CSS}</style>`);
  const stage = root.querySelector<HTMLElement>('[data-stage]')!;
  const params = root.querySelector<HTMLElement>('[data-params]')!;
  const tab = (m: Mode) => `<button type="button" role="tab" data-mode="${m}" title="${t.modeT[m]}" aria-label="${t.modeT[m]}">${MODE_ICON[m]}<span>${t.mode[m]}</span></button>`;
  const ROT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.4-5.7"/><path d="M20 4v4h-4"/></svg>';
  stage.classList.add('ct-stage');
  stage.insertAdjacentHTML('afterbegin',
    `<div class="ct-modes" role="tablist" data-modes>${tab('plane')}${tab('line')}${tab('surf')}${tab('bed')}</div>` +
    `<div class="ct-tool" data-tool hidden><button type="button" data-t="rotate" title="${t.toolRotate}" aria-label="${t.toolRotate}">${ROT}<span>${t.toolRotate}</span></button>` +
    `<button type="button" data-t="paint" title="${t.toolPaint}" aria-label="${t.toolPaint}">${MODE_ICON.brush}<span>${t.toolPaint}</span></button></div>` +
    `<button type="button" class="btn primary ct-go" data-go hidden></button><div class="ct-status" data-status role="status" aria-live="polite" hidden><i></i><span></span></div>`);
  const viewer = createStage(stage, lang).viewer;
  const view = new THREE.Group(), marks = new THREE.Group(), planMarks = new THREE.Group(), paintG = new THREE.Group(), knifeG = new THREE.Group(), pinG = new THREE.Group(), surfG = new THREE.Group();
  viewer.world.add(view, marks, planMarks, paintG, knifeG, pinG, surfG);

  // кнопку «i» к пояснениям добавляет страница — одинаково во всех генераторах
  const head = (text: string, _info = false, attr = '') => `<summary><b${attr}>${text}</b></summary>`;
  const tile = (k: string, v: string, label: string, icon = '') => `<button type="button" class="ct-tile" data-set="${k}:${v}">${icon}<span>${label}</span></button>`;
  const rng = (k: string, [min, max, step]: readonly number[], v: number, label: string, unit: string) => {
    const a = `min="${min}" max="${max}" step="${step}" value="${v}" aria-label="${label}" data-o="${k}"`;
    return `<div class="ct-row"><span>${label}</span><input type="range" ${a}><span class="num"><input type="number" ${a} inputmode="decimal"><i>${unit}</i></span></div>`;
  };
  params.innerHTML =
    `<details class="group" open>${head(t.file, true)}` +
    `<label class="ct-drop" data-drop><span class="btn primary">${t.pick}</span><small data-fname>${t.drop}</small><input type="file" accept=".3mf,.stl,.obj" hidden data-file></label>` +
    (sample ? `<button type="button" class="btn ghost ct-sample" data-sample>${t.sample}</button>` : '') +
    `<p class="hint">${t.local}</p></details>` +
    `<div data-work hidden>` +
    // ① где резать — у каждого режима своё
    `<details class="group" open>${head('', true, ' data-step1')}<p class="hint" data-more></p>` +
    `<p class="ct-note" data-state></p>` +
    `<div data-m="brush"><div class="ct-tiles">${tile('brush', 'paint', t.brushPaint)}${tile('brush', 'erase', t.brushErase)}</div>` +
    `${rng('brushR', BR, brushR, t.brushR, t.mm)}<div class="ct-links"><button type="button" class="ct-link" data-unpaint>${t.clearPaint}</button></div></div>` +
    `<div data-m="part brush"><div data-adj hidden>${rng('off', ADJ.off, 0, t.off, t.mm)}${rng('a', ADJ.a, 0, t.tiltA, '°')}${rng('b', ADJ.b, 0, t.tiltB, '°')}` +
    `<div class="ct-links"><button type="button" class="ct-link" data-reset>${t.reset}</button><button type="button" class="ct-link" data-clear>${t.clear}</button></div></div></div>` +
    `<div data-m="plane"><p class="ct-lbl">${t.axis}</p><div class="ct-tiles" style="--n:3">${tile('axis', '0', 'X')}${tile('axis', '1', 'Y')}${tile('axis', '2', 'Z')}</div>` +
    `${rng('pos', [0, 100, 0.5], 50, t.pos, t.mm)}${rng('pa', ADJ.a, 0, t.tiltA, '°')}${rng('pb', ADJ.b, 0, t.tiltB, '°')}` +
    `<div class="ct-links"><button type="button" class="ct-link" data-flip>${t.flip}</button><button type="button" class="ct-link" data-preset>${t.reset}</button></div></div>` +
    `<div data-m="line surf"><div class="ct-links"><button type="button" class="ct-link" data-lnundo>${t.lnUndo}</button><button type="button" class="ct-link" data-lnclear>${t.clearPaint}</button><button type="button" class="ct-link" data-lnflip>${t.flip}</button></div></div>` +
    `<div data-m="surf"><div class="ct-links"><button type="button" class="ct-link" data-ghost aria-pressed="false">${t.ghost}</button></div></div>` +
    `<div data-m="bed"><p class="ct-time" data-bed-is></p></div>` +
    `</details>` +
    // ② соединение
    `<details class="group" open data-joint-g>${head(t.joint, true)}` +
    `<div class="ct-tiles" data-joints>${tile('joint', 'none', t.jNone, ICON.none)}${tile('joint', 'lock', t.jLock, ICON.lock)}${tile('joint', 'tail', t.jTail, ICON.tail)}</div><p class="hint" data-jhint></p>` +
    `<div data-pins-g><p class="ct-lbl">${t.pins}</p><div class="ct-tiles" style="--n:3">${tile('pins', 'none', t.pNone, ICON.pNone)}${tile('pins', 'sep', t.pSep, ICON.pSep)}${tile('pins', 'built', t.pBuilt, ICON.pBuilt)}</div><p class="hint">${t.pinsHint}</p>` +
    `<div class="ct-pinrow" data-pinrow hidden><span data-pinn></span><span class="ct-links"><button type="button" class="ct-link" data-pinadd>${t.pinAdd}</button><button type="button" class="ct-link" data-pinreset>${t.pinReset}</button></span><p class="ct-sub">${t.pinHint}</p></div></div>` +
    `<p class="ct-note warn" data-warn hidden></p><p class="ct-time" data-time></p>` +
    `<details class="ct-fine"><summary>${t.fine}</summary>` +
    `<div data-f="lock">${rng('depth', LIM.depth, opts.depth, t.depth, t.mm)}<div data-f="rim">${rng('rim', LIM.rim, opts.rim, t.rim, t.mm)}</div><div data-f="angle">${rng('angle', LIM.angle, opts.angle, t.angle, '°')}<p class="ct-sub">${t.angleHint}</p></div><div data-f="area">${rng('area', LIM.area, opts.area, t.area, '%')}<p class="ct-sub">${t.areaHint}</p></div></div>` +
    `<div data-f="tail">${rng('tailD', LIM.tailD, opts.tailD, t.tailD, t.mm)}${rng('tailW', LIM.tailW, opts.tailW, t.tailW, '%')}</div>` +
    `<div data-f="pins">${rng('pinD', LIM.pinD, opts.pinD, t.pinD, t.mm)}${rng('pinH', LIM.pinH, opts.pinH, t.pinH, t.mm)}</div>` +
    `<div data-f="tol">${rng('tol', LIM.tol, opts.tol, t.tol, t.mm)}<p class="ct-sub">${t.tolShort}</p></div></details></details>` +
    // ③ как лечь
    `<details class="group" open data-place-g>${head(t.place)}<div class="ct-tiles">${tile('place', 'cut', t.plCut, ICON.plCut)}${tile('place', 'model', t.plModel, ICON.plModel)}</div></details>` +
    // ④ результат
    `<details class="group" open>${head(t.result)}<div data-list></div><button type="button" class="ct-link" data-undo hidden>${t.undo}</button>` +
    `<div class="ct-bar" data-bar hidden><i></i></div><p class="ct-msg" data-msg aria-live="polite"></p><p class="ct-note" data-dl-wait>${t.dlWait}</p></details>` +
    `</div>`;
  params.classList.add('ct-p');
  const $ = <E extends HTMLElement>(s: string) => params.querySelector<E>(s)!;
  const goBtn = stage.querySelector<HTMLButtonElement>('[data-go]')!;
  const status = (text: string) => { const s = stage.querySelector<HTMLElement>('[data-status]')!; s.hidden = !text; s.querySelector('span')!.textContent = text; };
  const msg = (text: string, bad = false) => { const m = $('[data-msg]'); m.textContent = text; m.className = 'ct-msg' + (bad ? ' bad' : ''); };
  const state = (text: string, warn = false) => { const s = $('[data-state]'); s.textContent = text; s.className = 'ct-note' + (warn ? ' warn' : ''); };
  const secs = (s: number) => s < 60 ? `${s < 10 ? (Math.round(s * 10) / 10).toLocaleString(lang) : Math.round(s)} ${t.s}` : `${(Math.round(s / 6) / 10).toLocaleString(lang)} ${t.min}`;

  // кнопки скачивания — только когда есть что скачивать
  const dl = () => root.querySelectorAll<HTMLButtonElement>('[data-download]');
  const ready = () => live.some((id) => all.get(id)!.kind !== 'body');
  const setReady = () => { const on = ready(); dl().forEach((b) => { b.disabled = !on; }); $('[data-dl-wait]').hidden = on; };
  new MutationObserver(() => { if (!ready()) dl().forEach((b) => { if (!b.disabled) b.disabled = true; }); })
    .observe(root, { subtree: true, attributes: true, attributeFilter: ['disabled'] });

  // ---------- поток ----------
  let worker: Worker | null = null;
  const workerURL = import.meta.url.replace(/[^/]*$/, 'worker.js');
  const mfURL = import.meta.resolve('manifold-3d/manifold');
  let onRes: ((r: Res) => void) | null = null;
  const send = (q: Req, tr: Transferable[] = []) => {
    if (!worker) {
      worker = new Worker(workerURL, { type: 'module' });
      worker.onmessage = (e: MessageEvent<Res>) => {
        const r = e.data;
        if (r.t === 'side') onSide(r);
        else if (r.t === 'plan') onPlan(r);
        else if (r.t === 'grid') onGrid(r);
        else if (r.t === 'kplan') onKplan(r);
        else if (r.t === 'splan') onSplan(r);
        else if (onRes) onRes(r);
        else if (r.t === 'error') onLooseError(r.code);
      };
      worker.onerror = (e) => { console.error(e); (onRes ?? ((r: Res) => r.t === 'error' && onLooseError(r.code)))({ t: 'error', code: 'FAILED' }); };
    }
    worker.postMessage(q, tr);
  };
  const ask = <K extends Res['t']>(q: Req, want: K, tr: Transferable[] = [], prog?: (k: number) => void) =>
    new Promise<Extract<Res, { t: K }>>((ok, fail) => {
      onRes = (r) => {
        if (r.t === 'progress') prog?.(r.k);
        else if (r.t === 'error') { onRes = null; if (r.detail) console.error('worker:', r.detail); fail(new Error(r.code)); }
        else if (r.t === want) { onRes = null; ok(r as Extract<Res, { t: K }>); }
      };
      send(q, tr);
    });
  const errText = (e: unknown) => {
    const [c, n] = ((e as Error)?.message ?? '').split(':');
    const s = t.err[c as keyof typeof t.err] ?? t.err.FAILED;
    return s.replace('{n}', n ? (c === 'TOO_BIG' ? (Math.round(+n / 1e5) / 10).toLocaleString(lang) : n) : '');
  };
  /** Ошибка запроса без ожидания (превью разрезов под стол): показать в шаге ①. */
  function onLooseError(code: string) {
    status('');
    if (mode === 'bed') { grid = null; clearMarks(); state(errText(new Error(code)), true); updateGo(); }
    if (mode === 'surf') { sfOk = false; clearMarks(); state(errText(new Error(code)), true); updateGo(); }
  }

  // ---------- состояние ----------
  let fileName = 'model';
  let lastFile: File | null = null;
  let shift: V3 = [0, 0, 0];
  let plate = 0;
  /** Все тела, что есть сейчас или в истории отмены; live — на столе сейчас. */
  const all = new Map<number, BodyMsg>();
  let live: number[] = [];
  const hist: number[][] = [];
  let busy = false;
  /** «Частину»: тело, плоскость шейки, кончик и поправки; c — середина среза (вокруг неё наклон). */
  type Sel = { id: number; base: Plane; tip: V3; found: boolean; c: V3 | null; off: number; a: number; b: number; src: 'part' | 'brush' };
  let sel: Sel | null = null;
  /** «Площиною»: тело, ось, высота от низа тела по оси, наклоны, с какой стороны деталь. */
  const pl = { id: 0, axis: 2 as 0 | 1 | 2, pos: 0, a: 0, b: 0, flip: false };
  /** «Під стіл»: разрезы и число частей по осям (null — не посчитано). */
  let grid: { cuts: GridCut[]; counts: V3 } | null = null;
  let bed = { x: 256, y: 256, z: 256 };
  let seq = 0, sideTimer = 0, planTimer = 0;
  /** Что сейчас показано в превью: где резать (для разреза — ровно то же). */
  let where: Where | null = null;
  let warnings: string[] = [];
  let lastPv = -1;
  /** Штифты, поставленные человеком (null — автоматически), показанные сейчас; ждём нажатия, чтобы поставить. */
  let pinAt: V3[] | null = null, pinShow: V3[] = [], pinArm = false;

  // ---------- просмотр ----------
  const geoms = new Map<number, { attr: THREE.BufferAttribute; geo: THREE.BufferGeometry }>();
  const geomOf = (b: BodyMsg) => {
    let g = geoms.get(b.id);
    if (!g) {
      const attr = new THREE.BufferAttribute(b.pos, 3), geo = new THREE.BufferGeometry();
      geo.setAttribute('position', attr);
      geo.setIndex(new THREE.BufferAttribute(b.tri, 1));
      geoms.set(b.id, (g = { attr, geo }));
    }
    return g;
  };
  const mats = new Map<string, THREE.MeshStandardMaterial>();
  const mat = (k: string, color?: string) => {
    let m = mats.get(k);
    if (!m) {
      const c = color ?? (k === 'cut' ? CUT : css(...COL[k as BodyMsg['kind']]));
      m = new THREE.MeshStandardMaterial({ color: new THREE.Color(c), roughness: 0.6, metalness: 0, flatShading: true });
      if (color || k === 'cut') { m.polygonOffset = true; m.polygonOffsetFactor = -1; m.polygonOffsetUnits = -1; }
      mats.set(k, m);
    }
    return m;
  };
  window.addEventListener('theme', () => { for (const m of mats.values()) m.dispose(); mats.clear(); draw(); refresh(true); });

  function draw() {
    view.clear();
    for (const id of live) {
      const b = all.get(id)!;
      const m = new THREE.Mesh(geomOf(b).geo, mat(b.kind));
      m.userData.id = id;
      view.add(m);
    }
    applyGhost();
    showSize();
    renderList();
  }
  function bounds(ids = live) {
    const lo = [Infinity, Infinity, Infinity], hi = [-Infinity, -Infinity, -Infinity];
    for (const id of ids) {
      const p = all.get(id)!.pos;
      for (let i = 0; i < p.length; i += 3) for (let c = 0; c < 3; c++) { const v = p[i + c]; if (v < lo[c]) lo[c] = v; if (v > hi[c]) hi[c] = v; }
    }
    return { lo, hi };
  }
  /** Камера на всё, что на столе: детали ложатся справа от модели, за край стола. */
  function frameAll() {
    const { lo, hi } = bounds();
    viewer.frame(2 * Math.max(Math.abs(lo[0]), Math.abs(hi[0])), 2 * Math.max(Math.abs(lo[1]), Math.abs(hi[1])), hi[2] - lo[2]);
  }
  function showSize() {
    const d = stage.querySelector<HTMLElement>('[data-dims]')!;
    if (!live.length) { d.hidden = true; return; }
    const { lo, hi } = bounds();
    d.hidden = false;
    d.innerHTML = [0, 1, 2].map((c) => `<b>${Math.round((hi[c] - lo[c]) * 10) / 10}</b>`).join(' × ') + ` ${t.mm}`;
  }
  function renderList() {
    $('[data-list]').innerHTML = live.map((id) => {
      const b = all.get(id)!;
      return `<div class="ct-body"><i style="background:${css(...COL[b.kind])}"></i><span>${b.name} · ${t.kind[b.kind]}</span><small>${(b.vol / 1000).toLocaleString(lang, { maximumFractionDigits: 1 })} ${t.vol}</small></div>`;
    }).join('');
    $('[data-undo]').hidden = !hist.length;
    setReady();
  }

  /** Метки превью: отрезаемое (оранжевым), линия среза, диск плоскости; планы соединения — отдельно. */
  const clearGroup = (g: THREE.Group) => {
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry && !m.userData.sharedGeo) m.geometry.dispose();
      if (m.material && !m.userData.sharedMat) (m.material as THREE.Material).dispose();
    });
    g.clear();
  };
  const clearMarks = () => { clearGroup(marks); clearGroup(planMarks); clearGroup(pinG); pinShow = []; };
  const subset = (b: BodyMsg, tris: ArrayLike<number>) => {
    const idx = new Uint32Array(tris.length * 3);
    for (let i = 0; i < tris.length; i++) { const k = tris[i] * 3; idx[i * 3] = b.tri[k]; idx[i * 3 + 1] = b.tri[k + 1]; idx[i * 3 + 2] = b.tri[k + 2]; }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', geomOf(b).attr);
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    return g;
  };
  function addDisc(c: V3, n: V3, r: number, color = 0xd32f2f) {
    const disc = new THREE.Mesh(new THREE.CircleGeometry(r, 48),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.16, side: THREE.DoubleSide, depthWrite: false }));
    disc.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(...n));
    disc.position.set(...c);
    marks.add(disc);
  }
  function showSide(id: number, tris: Uint32Array, line: Float32Array, p: Plane) {
    clearGroup(marks);
    const b = all.get(id);
    if (!b) return;
    const hl = new THREE.Mesh(subset(b, tris), mat('cut'));
    hl.userData.sharedMat = true;
    marks.add(hl);
    if (!line.length) return;
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.BufferAttribute(line, 3));
    const ln = new THREE.LineSegments(lg, new THREE.LineBasicMaterial({ color: 0xd32f2f, depthTest: false }));
    ln.renderOrder = 2;
    marks.add(ln);
    // диск: в середине линии, радиус — до её дальней точки с запасом
    const c: V3 = [0, 0, 0], n = line.length / 3;
    for (let i = 0; i < line.length; i += 3) { c[0] += line[i] / n; c[1] += line[i + 1] / n; c[2] += line[i + 2] / n; }
    let r = 0;
    for (let i = 0; i < line.length; i += 3) r = Math.max(r, Math.hypot(line[i] - c[0], line[i + 1] - c[1], line[i + 2] - c[2]));
    addDisc(c, p.n, r * 1.25 + 3);
    if (sel && sel.id === id && !sel.c) sel.c = c;
  }
  function showPlan(lines: Float32Array) {
    clearGroup(planMarks);
    if (!lines.length) return;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(lines, 3));
    const l = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: 0xffd54f, depthTest: false }));
    l.renderOrder = 3;
    planMarks.add(l);
  }

  // ---------- оценка времени ----------
  const trisOf = (id: number) => (all.get(id)?.tri.length ?? 0) / 3;
  const factor = () => 1 + (opts.joint !== 'none' ? 0.25 : 0) + (opts.pins !== 'none' ? 0.15 : 0);
  /** Сколько «работы» в разрезе (треугольники с поправкой на соединение) — время = K × работа. */
  function work() {
    if (mode === 'bed') {
      if (!grid?.cuts.length) return 0;
      const tris = live.filter((id) => all.get(id)!.kind !== 'pin').reduce((s, id) => s + trisOf(id), 0);
      // каждый разрез проходит не через всю модель, а через её часть
      return tris * factor() * (0.6 * grid.cuts.length + 0.4);
    }
    if (mode === 'surf') return sf && sfOk ? trisOf(sf.id) * 0.6 : 0;
    if (mode === 'line') return kn && knOk ? trisOf(kn.id) * 1.6 * (opts.joint === 'lock' ? (opts.area < 100 ? 5.5 : 2.4) : 1) * (opts.pins !== 'none' ? 1.15 : 1) : 0;
    return where ? trisOf(where.id) * factor() : 0;
  }
  function updateGo() {
    const est = K * work();
    let label = '';
    if (mode === 'bed') { const n = grid ? grid.counts[0] * grid.counts[1] * grid.counts[2] : 0; label = n > 1 ? t.cut.bed.replace('{n}', String(n)) : ''; }
    else if (mode === 'line') label = kn && knOk ? t.cut.line : '';
    else if (mode === 'surf') label = sf && sfOk ? t.cut.surf : '';
    else if (where && !warnings.includes('noCut')) label = t.cut[mode];
    goBtn.hidden = !label || busy;
    goBtn.textContent = label ? `${label} · ≈ ${secs(est)}` : '';
    const pv = lastPv >= 0 ? t.pvTime.replace('{t}', secs(lastPv / 1000)) : '';
    $('[data-time]').textContent = [pv, label ? t.estIs.replace('{t}', secs(est)) : ''].filter(Boolean).join(' · ');
  }

  // ---------- превью ----------
  /** «Частину»: плоскость с поправками — наклон вокруг середины среза, сдвиг вдоль нормали. */
  function partPlane(s: Sel): Plane {
    const n = tilt(s.base.n, s.a, s.b);
    const c = s.c ?? ([s.base.n[0] * s.base.s, s.base.n[1] * s.base.s, s.base.n[2] * s.base.s] as V3);
    return { n, s: dot(n, c) + s.off };
  }
  /** «Площиною»: границы тела по оси. */
  function planeBox() {
    const { lo, hi } = bounds([pl.id]);
    return { lo, hi, size: hi[pl.axis] - lo[pl.axis] };
  }
  /** Плоскость через точку на высоте pos (поперёк — середина тела). */
  function planePlane(): Plane {
    const { lo, hi } = planeBox();
    const c = [0, 1, 2].map((k) => (lo[k] + hi[k]) / 2) as V3;
    c[pl.axis] = lo[pl.axis] + pl.pos;
    const n0: V3 = [0, 0, 0];
    n0[pl.axis] = 1;
    let n = tilt(n0, pl.a, pl.b);
    if (pl.flip) n = [-n[0], -n[1], -n[2]];
    return { n, s: dot(n, c) };
  }
  function whereNow(): Where | null {
    if (mode === 'part' || mode === 'brush') return sel ? { id: sel.id, plane: partPlane(sel), tip: sel.tip } : null;
    if (mode === 'plane') return all.has(pl.id) && live.includes(pl.id) ? { id: pl.id, plane: planePlane(), tip: null } : null;
    return null;
  }
  const sendPlan = () => { if (where && !busy) send({ t: 'plan', seq, opts: { ...opts }, at: pinAt, ...where }); };
  /** Пересчитать превью: срез — сразу (сотые секунды), соединение — после паузы (десятые). */
  function refresh(now = false) {
    clearTimeout(sideTimer);
    clearTimeout(planTimer);
    if (mode === 'bed') { requestGrid(); return; }
    if (mode === 'line') { where = null; knifeChanged(); return; }
    if (mode === 'surf') { where = null; surfChanged(); return; }
    where = whereNow();
    if (!where) { clearMarks(); setPins(new Float32Array(0)); updateGo(); return; }
    const go = () => {
      if (busy || !where) return;
      send({ t: 'side', seq: ++seq, ...where });
      planTimer = window.setTimeout(sendPlan, 220);
    };
    if (now) go(); else sideTimer = window.setTimeout(go, 50);
    updateGo();
  }
  const refreshPlan = () => {
    clearTimeout(planTimer);
    if (mode === 'bed') { updateGo(); return; }
    if (mode === 'line') { knifeChanged(); return; }
    if (mode === 'surf') { surfChanged(); return; }
    planTimer = window.setTimeout(sendPlan, 150);
    updateGo();
  };
  function onSide(r: Extract<Res, { t: 'side' }>) {
    if (r.seq !== seq || !where || mode === 'bed') return;
    showSide(r.id, r.tris, r.line, where.plane);
  }
  function onPlan(r: Extract<Res, { t: 'plan' }>) {
    if (r.seq !== seq || mode === 'bed') return;
    showPlan(r.lines);
    setPins(r.pins);
    lastPv = r.ms;
    warnings = r.notes;
    showWarn();
    updateGo();
  }
  function showWarn() {
    const w = $('[data-warn]');
    w.hidden = !warnings.length;
    w.textContent = warnings.map((k) => t.warn[k as keyof typeof t.warn] ?? '').join(' ');
  }
  function requestGrid() {
    clearMarks();
    grid = null;
    updateGo();
    if (!live.length || busy) return;
    // каждая часть уже помещается (поделили раньше) — резать нечего; запас 4 мм, как в потоке
    const fit = live.filter((id) => all.get(id)!.kind !== 'pin').every((id) => {
      const { lo, hi } = bounds([id]);
      return hi[0] - lo[0] <= bed.x - 4 && hi[1] - lo[1] <= bed.y - 4 && hi[2] - lo[2] <= bed.z - 4;
    });
    if (fit) { state(t.fits); lastPv = -1; updateGo(); return; }
    status(t.counting);
    send({ t: 'grid', seq: ++seq, bed: [bed.x, bed.y, bed.z] });
  }
  function onGrid(r: Extract<Res, { t: 'grid' }>) {
    if (r.seq !== seq || mode !== 'bed') return;
    status('');
    grid = { cuts: r.cuts, counts: r.counts };
    lastPv = r.ms;
    const n = r.counts[0] * r.counts[1] * r.counts[2];
    const d = r.counts.map((c, a) => (c > 1 ? t.perAxis.replace('{n}', String(c)).replace('{a}', AX[a]) : '')).filter(Boolean).join(' × ');
    state(n > 1 ? t.willBe.replace('{n}', String(n)).replace('{d}', d) : t.fits);
    // части разными цветами поверх модели, плоскости разрезов — дисками
    clearMarks();
    if (n > 1) {
      for (const { id, cell } of r.cells) {
        const b = all.get(id);
        if (!b) continue;
        const by = new Map<number, number[]>();
        cell.forEach((c, i) => { let a = by.get(c); if (!a) by.set(c, (a = [])); a.push(i); });
        for (const [c, ids] of by) {
          const k = c % CELL_COL.length;
          const m = new THREE.Mesh(subset(b, ids), mat('cell' + k, CELL_COL[k]));
          m.userData.sharedMat = true;
          marks.add(m);
        }
      }
      const { lo, hi } = bounds(live.filter((id) => all.get(id)!.kind !== 'pin'));
      for (const g of r.cuts) {
        // плоскость разреза — прямоугольник по габариту модели с запасом
        const c = [0, 1, 2].map((k) => (lo[k] + hi[k]) / 2) as V3;
        c[g.axis] = g.at;
        const [i, j] = [0, 1, 2].filter((k) => k !== g.axis), sz = [0, 0, 0];
        sz[i] = hi[i] - lo[i] + 12; sz[j] = hi[j] - lo[j] + 12; sz[g.axis] = 0.01;
        const box = new THREE.Mesh(new THREE.BoxGeometry(sz[0], sz[1], sz[2]),
          new THREE.MeshBasicMaterial({ color: 0x1e88e5, transparent: true, opacity: 0.18, side: THREE.DoubleSide, depthWrite: false }));
        box.position.set(...c);
        marks.add(box);
      }
    }
    updateGo();
  }

  // ---------- режимы ----------
  function setMode(m: Mode) {
    mode = m;
    pinAt = null; pinArm = false;
    save();
    stage.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.mode === m)));
    params.querySelectorAll<HTMLElement>('[data-m]').forEach((el) => { el.hidden = !el.dataset.m!.split(' ').includes(m); });
    // выбор «Частину» и закраска «Кисті» — разные: при смене режима чужой снимается
    if (sel && sel.src !== m) { sel = null; where = null; $('[data-adj]').hidden = true; }
    stage.querySelector<HTMLElement>('[data-tool]')!.hidden = m !== 'brush';
    applyControls();
    paintG.visible = m === 'brush' && !sel;
    $('[data-step1]').textContent = t.step1[m];
    $('[data-more]').textContent = t.more[m];
    $('[data-place-g]').hidden = m === 'bed' || m === 'line';
    knifeG.visible = m === 'line';
    surfG.visible = m === 'surf';
    applyGhost();
    // хвост — только у среза плоскостью
    if (m !== 'plane' && opts.joint === 'tail') opts.joint = 'lock';
    syncTiles();
    warnings = []; showWarn(); lastPv = -1;
    if (m === 'part') state(sel ? (sel.found ? t.found : t.notFound) : t.hint.part, !!sel && !sel.found);
    if (m === 'brush') state(sel ? t.painted : t.hint.brush);
    if (m === 'line') { lnState(); drawKnife(); }
    if (m === 'surf') { sfState(); drawSurf(); }
    if (m === 'plane') { state(t.hint.plane); if (!live.includes(pl.id)) planeInit(); else syncPlane(); }
    if (m === 'bed') { state(t.hint.bed); $('[data-bed-is]').textContent = t.bedIs.replace('{s}', `${bed.x} × ${bed.y} × ${bed.z}`); }
    clearMarks();
    refresh(true);
  }
  stage.querySelector('[data-modes]')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-mode]');
    if (b && !busy && b.dataset.mode !== mode) { msg(''); setMode(b.dataset.mode as Mode); }
  });
  /** «Площиною» по самому большому телу, посередине. */
  function planeInit(id?: number) {
    pinAt = null;
    const cand = live.filter((x) => all.get(x)!.kind !== 'pin');
    if (!cand.length) return;
    pl.id = id ?? cand.reduce((a, b) => (all.get(b)!.vol > all.get(a)!.vol ? b : a));
    pl.a = pl.b = 0; pl.flip = false;
    pl.pos = planeBox().size / 2;
    syncPlane();
  }
  function syncPlane() {
    const size = planeBox().size;
    pl.pos = Math.min(size, Math.max(0, pl.pos));
    params.querySelectorAll<HTMLInputElement>('[data-o="pos"]').forEach((i) => { i.max = String(Math.round(size * 10) / 10); i.value = String(Math.round(pl.pos * 10) / 10); });
    params.querySelectorAll<HTMLInputElement>('[data-o="pa"]').forEach((i) => { i.value = String(pl.a); });
    params.querySelectorAll<HTMLInputElement>('[data-o="pb"]').forEach((i) => { i.value = String(pl.b); });
    syncTiles();
  }

  // ---------- «Частину» ----------
  const setAdj = (k: string, v: number) => params.querySelectorAll<HTMLInputElement>(`[data-o="${k}"]`).forEach((i) => { i.value = String(v); });
  function select(s: Sel | null) {
    sel = s;
    pinAt = null;
    $('[data-adj]').hidden = !s;
    for (const k of ['off', 'a', 'b']) setAdj(k, 0);
    warnings = []; showWarn(); lastPv = -1;
    if (!s) { clearMarks(); where = null; updateGo(); if (mode === 'part' || mode === 'brush') state(t.hint[mode]); return; }
    state(s.src === 'brush' ? t.painted : s.found ? t.found : t.notFound, !s.found);
    refresh(true);
  }
  async function pickAt(id: number, p: V3) {
    if (busy) return;
    busy = true;
    status(t.finding);
    try {
      const r = await ask({ t: 'neck', id, p }, 'neck');
      busy = false;
      select({ id, base: r.plane, tip: r.tip, found: r.found, c: null, off: 0, a: 0, b: 0, src: 'part' });
    } catch (e) { console.error(e); msg(errText(e), true); busy = false; }
    status('');
  }

  // ---------- клик по модели ----------
  let down: { x: number; y: number; t: number; id: number } | null = null;
  stage.addEventListener('pointerdown', (e) => { if (e.isPrimary && e.button === 0) down = { x: e.clientX, y: e.clientY, t: performance.now(), id: e.pointerId }; });
  stage.addEventListener('pointerup', (e) => {
    const d = down;
    down = null;
    if (!d || d.id !== e.pointerId || busy || Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6 || performance.now() - d.t > 600) return;
    if ((e.target as HTMLElement).closest('button')) return;
    if (pinArm) { placePin(e); return; }
    if (mode === 'brush') return;
    if (mode === 'line') { addPoint(e); return; }
    if (mode === 'surf') { addSurfPoint(e); return; }
    const h = viewer.raycaster(e).intersectObjects(view.children, false)[0];
    if (!h) return;
    const q = viewer.world.worldToLocal(h.point.clone());
    const id = h.object.userData.id as number;
    if (mode === 'part') void pickAt(id, [q.x, q.y, q.z]);
    else if (mode === 'plane' && all.get(id)!.kind !== 'pin') {
      // срез через точку нажатия, по выбранной оси
      if (id !== pl.id) planeInit(id);
      pl.pos = [q.x, q.y, q.z][pl.axis] - planeBox().lo[pl.axis];
      pl.a = pl.b = 0;
      syncPlane();
      refresh(true);
    }
  });

  // ---------- «Кисть» ----------
  // Закраска — по вершинам тела, расходится по сетке от точки касания (не сквозь тонкие стенки),
  // в пределах капсулы вдоль мазка. По краю закраски — плоскость (метод главных компонент).
  type Paint = { id: number; on: Uint8Array; off: Uint32Array; nb: Uint32Array };
  let paint: Paint | null = null;
  let erase = false, painting = true;
  let stroke: { last: V3 | null; ev: PointerEvent | null; raf: number } | null = null;
  const pointers = new Set<number>();
  let stamp = new Uint32Array(0), stampN = 0;
  const ctrl = viewer.controls;
  const defMouse = { ...ctrl.mouseButtons }, defTouch = { ...ctrl.touches };
  /** В режиме «фарбувати» левая кнопка и один палец красят; крутит правая кнопка и два пальца. */
  function applyControls() {
    const on = mode === 'brush' && painting;
    Object.assign(ctrl.mouseButtons, on ? { LEFT: null, MIDDLE: defMouse.MIDDLE, RIGHT: THREE.MOUSE.ROTATE } : defMouse);
    Object.assign(ctrl.touches, on ? { ONE: null, TWO: THREE.TOUCH.DOLLY_ROTATE } : defTouch);
    stage.classList.toggle('ct-paint', on);
    stage.querySelectorAll<HTMLButtonElement>('[data-t]').forEach((b) => b.setAttribute('aria-pressed', String((b.dataset.t === 'paint') === painting)));
  }
  function setTool(p: boolean) { painting = p; applyControls(); }
  stage.querySelector('[data-tool]')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-t]');
    if (b) setTool(b.dataset.t === 'paint');
  });
  /** Соседи вершин (CSR) — один раз на тело. */
  function paintOf(id: number): Paint {
    if (paint?.id === id) return paint;
    const b = all.get(id)!, nv = b.pos.length / 3, T = b.tri;
    const off = new Uint32Array(nv + 1);
    for (let i = 0; i < T.length; i++) off[T[i] + 1] += 2;
    for (let i = 0; i < nv; i++) off[i + 1] += off[i];
    const nb = new Uint32Array(off[nv]), fill = off.slice(0, nv);
    for (let t = 0; t < T.length; t += 3) {
      const a = T[t], c = T[t + 1], d = T[t + 2];
      nb[fill[a]++] = c; nb[fill[a]++] = d; nb[fill[c]++] = a; nb[fill[c]++] = d; nb[fill[d]++] = a; nb[fill[d]++] = c;
    }
    if (stamp.length < nv) { stamp = new Uint32Array(nv); stampN = 0; }
    return (paint = { id, on: new Uint8Array(nv), off, nb });
  }
  /** Закрасить (стереть) вершины, связные с seeds, не дальше R от отрезка a–b. */
  function dab(P: Paint, seeds: number[], a: V3, b: V3) {
    const pos = all.get(P.id)!.pos, R2 = brushR * brushR, v = erase ? 0 : 1;
    const ab: V3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]], L2 = dot(ab, ab) || 1;
    const near = (i: number) => {
      const x = pos[i * 3] - a[0], y = pos[i * 3 + 1] - a[1], z = pos[i * 3 + 2] - a[2];
      const k = Math.min(1, Math.max(0, (x * ab[0] + y * ab[1] + z * ab[2]) / L2));
      return (x - ab[0] * k) ** 2 + (y - ab[1] * k) ** 2 + (z - ab[2] * k) ** 2 <= R2;
    };
    if (++stampN === 0xffffffff) { stamp.fill(0); stampN = 1; }
    const st: number[] = [];
    for (const s of seeds) if (stamp[s] !== stampN) { stamp[s] = stampN; st.push(s); }
    while (st.length) {
      const i = st.pop()!;
      P.on[i] = v;
      for (let k = P.off[i]; k < P.off[i + 1]; k++) {
        const j = P.nb[k];
        if (stamp[j] !== stampN) { stamp[j] = stampN; if (near(j)) st.push(j); }
      }
    }
  }
  /** Закраска на экране: треугольники, у которых закрашены хотя бы две вершины. */
  function drawPaint() {
    clearGroup(paintG);
    if (!paint) return;
    const b = all.get(paint.id)!, on = paint.on, T = b.tri, ids: number[] = [];
    for (let t = 0; t < T.length / 3; t++) if (on[T[t * 3]] + on[T[t * 3 + 1]] + on[T[t * 3 + 2]] >= 2) ids.push(t);
    if (!ids.length) return;
    const m = new THREE.Mesh(subset(b, ids), mat('paint', '#3B82F6'));
    m.userData.sharedMat = true;
    m.renderOrder = 1;
    paintG.add(m);
  }
  function clearPaint() { paint = null; clearGroup(paintG); }
  function paintStep() {
    if (!stroke?.ev) return;
    const e = stroke.ev;
    stroke.ev = null; stroke.raf = 0;
    const h = viewer.raycaster(e).intersectObjects(view.children, false)[0];
    const id = h?.object.userData.id as number | undefined;
    if (!h || id === undefined || all.get(id)!.kind === 'pin' || !h.face) { stroke.last = null; return; }
    const q = viewer.world.worldToLocal(h.point.clone()), p: V3 = [q.x, q.y, q.z];
    const P = paintOf(id);
    dab(P, [h.face.a, h.face.b, h.face.c], stroke.last ?? p, p);
    stroke.last = p;
    drawPaint();
  }
  const queue = (e: PointerEvent) => { if (!stroke) return; stroke.ev = e; if (!stroke.raf) stroke.raf = requestAnimationFrame(paintStep); };
  stage.addEventListener('pointerdown', (e) => {
    pointers.add(e.pointerId);
    if (pointers.size > 1) { endStroke(); return; }
    if (mode !== 'brush' || !painting || busy || e.button !== 0 || !(e.target instanceof HTMLCanvasElement)) return;
    // новый мазок: превью среза прячем, закраску показываем
    clearMarks(); paintG.visible = true;
    stroke = { last: null, ev: e, raf: 0 };
    // первую точку — сразу: иначе её перезапишет движение до следующего кадра
    paintStep();
  });
  stage.addEventListener('pointermove', (e) => { if (stroke && pointers.size === 1) queue(e); });
  for (const ev of ['pointerup', 'pointercancel'] as const) window.addEventListener(ev, (e) => { pointers.delete(e.pointerId); if (stroke) endStroke(); });
  function endStroke() {
    if (!stroke) return;
    if (stroke.raf) cancelAnimationFrame(stroke.raf);
    if (stroke.ev) paintStep();
    stroke = null;
    const s = fitPaint();
    if (s) { paintG.visible = false; select(s); return; }
    sel = null; where = null; $('[data-adj]').hidden = true; clearMarks(); updateGo();
    const any = !!paint && paint.on.some((x) => x === 1);
    state(any ? t.paintAll : t.hint.brush, any);
  }
  /**
   * Плоскость по закраске. Красят обычно видимую сторону, поэтому край закраски лежит на поверхности
   * и плоскостью через него не годится. Срез — поперёк закрашенного: нормаль вдоль его длинной оси
   * (если закраска круглая — от середины модели к закраске), плоскость — у края, ближнего к модели.
   */
  function fitPaint(): Sel | null {
    if (!paint) return null;
    const on = paint.on, pos = all.get(paint.id)!.pos, nv = on.length;
    const S = [0, 0, 0], Q = [0, 0, 0, 0, 0, 0], bc = [0, 0, 0];
    let np = 0;
    for (let i = 0; i < nv; i++) {
      const x = pos[i * 3], y = pos[i * 3 + 1], z = pos[i * 3 + 2];
      bc[0] += x; bc[1] += y; bc[2] += z;
      if (!on[i]) continue;
      S[0] += x; S[1] += y; S[2] += z;
      Q[0] += x * x; Q[1] += y * y; Q[2] += z * z; Q[3] += x * y; Q[4] += x * z; Q[5] += y * z;
      np++;
    }
    // ничего или всё закрашено — резать нечего
    if (np < 3 || np === nv) return null;
    const pc: V3 = [S[0] / np, S[1] / np, S[2] / np];
    const C = [
      [Q[0] / np - pc[0] * pc[0], Q[3] / np - pc[0] * pc[1], Q[4] / np - pc[0] * pc[2]],
      [Q[3] / np - pc[0] * pc[1], Q[1] / np - pc[1] * pc[1], Q[5] / np - pc[1] * pc[2]],
      [Q[4] / np - pc[0] * pc[2], Q[5] / np - pc[1] * pc[2], Q[2] / np - pc[2] * pc[2]],
    ];
    const out: V3 = unit([pc[0] - bc[0] / nv, pc[1] - bc[1] / nv, pc[2] - bc[2] / nv]);
    const ax = smallestEig(C.map((r) => r.map((x) => -x)));
    const l1 = [0, 1, 2].reduce((s, i) => s + ax[i] * [0, 1, 2].reduce((q, j) => q + C[i][j] * ax[j], 0), 0);
    // вытянутая закраска — по её оси, наружу от модели
    let n: V3 = l1 > 0.5 * (C[0][0] + C[1][1] + C[2][2]) ? ax : out;
    if (dot(n, out) < 0) n = [-n[0], -n[1], -n[2]];
    let lo = Infinity, hi = -Infinity, tip: V3 = pc;
    for (let i = 0; i < nv; i++) if (on[i]) {
      const d = pos[i * 3] * n[0] + pos[i * 3 + 1] * n[1] + pos[i * 3 + 2] * n[2];
      if (d < lo) lo = d;
      if (d > hi) { hi = d; tip = [pos[i * 3], pos[i * 3 + 1], pos[i * 3 + 2]]; }
    }
    // середина среза (вокруг неё наклон) — закрашенное у края
    const c: V3 = [0, 0, 0];
    let k = 0;
    const band = Math.max(1, brushR / 2);
    for (let i = 0; i < nv; i++) if (on[i] && pos[i * 3] * n[0] + pos[i * 3 + 1] * n[1] + pos[i * 3 + 2] * n[2] < lo + band) {
      c[0] += pos[i * 3]; c[1] += pos[i * 3 + 1]; c[2] += pos[i * 3 + 2]; k++;
    }
    c[0] /= k; c[1] /= k; c[2] /= k;
    const s = lo + 0.2;
    return { id: paint.id, base: { n, s }, tip, found: true, c: [c[0] + n[0] * (s - dot(n, c)), c[1] + n[1] * (s - dot(n, c)), c[2] + n[2] * (s - dot(n, c))], off: 0, a: 0, b: 0, src: 'brush' };
  }
  $('[data-unpaint]').onclick = () => { clearPaint(); select(null); };

  // ---------- «Лінією» ----------
  // Точки ставятся на плоскость, перпендикулярную взгляду в момент первой точки; через них — плавная
  // кривая (Кэтмелл — Ром, центростремительная). Нож — эта кривая, вытянутая вдоль взгляда сквозь модель.
  type KnifeUI = { id: number; d: V3; o: V3; pts: V3[]; closed: boolean; flip: boolean };
  let kn: KnifeUI | null = null;
  /** Лента ножа на экране — по ней двигаются штифты. */
  let ribbon: THREE.Mesh | null = null;
  /** Превью ответило, что разрез возможен. */
  let knOk = false;
  let hover: V3 | null = null, hoverRaf = 0, knTimer = 0;
  const v3 = (p: THREE.Vector3): V3 => [p.x, p.y, p.z];
  /** Направление взгляда в осях модели. */
  const viewDir = (): V3 => {
    const d = viewer.camera.getWorldDirection(new THREE.Vector3());
    return unit(v3(d.applyQuaternion(viewer.world.getWorldQuaternion(new THREE.Quaternion()).invert())));
  };
  const localRay = (e: { clientX: number; clientY: number }) =>
    viewer.raycaster(e).ray.clone().applyMatrix4(viewer.world.matrixWorld.clone().invert());
  /** Точка луча на плоскости ножа. */
  function onKnifePlane(e: { clientX: number; clientY: number }): V3 | null {
    if (!kn) return null;
    const r = localRay(e), o = v3(r.origin), dir = v3(r.direction), den = dot(dir, kn.d);
    if (Math.abs(den) < 0.05) return null;
    const k = dot([kn.o[0] - o[0], kn.o[1] - o[1], kn.o[2] - o[2]], kn.d) / den;
    return [o[0] + dir[0] * k, o[1] + dir[1] * k, o[2] + dir[2] * k];
  }
  /** Точка на экране, px от угла холста. */
  function toScreen(p: V3) {
    const q = viewer.world.localToWorld(new THREE.Vector3(...p)).project(viewer.camera), r = viewer.canvas.getBoundingClientRect();
    return [r.left + ((q.x + 1) / 2) * r.width, r.top + ((1 - q.y) / 2) * r.height];
  }
  const centerOf = (id: number) => { const { lo, hi } = bounds([id]); return { c: [0, 1, 2].map((k) => (lo[k] + hi[k]) / 2) as V3, R: Math.hypot(hi[0] - lo[0], hi[1] - lo[1], hi[2] - lo[2]) / 2 }; };
  function addPoint(e: PointerEvent) {
    if (busy) return;
    if (!kn) {
      const h = viewer.raycaster(e).intersectObjects(view.children, false)[0];
      const hid = h?.object.userData.id as number | undefined;
      const cand = live.filter((x) => all.get(x)!.kind !== 'pin');
      if (!cand.length) return;
      const id = hid !== undefined && all.get(hid)!.kind !== 'pin' ? hid : cand.reduce((a, b) => (all.get(b)!.vol > all.get(a)!.vol ? b : a));
      const d = viewDir();
      const o = h && hid === id ? v3(viewer.world.worldToLocal(h.point.clone())) : centerOf(id).c;
      kn = { id, d, o, pts: [], closed: false, flip: false };
      pinAt = null;
      const p = onKnifePlane(e) ?? o;
      kn.pts.push(p);
    } else {
      if (kn.closed) return;
      const f = toScreen(kn.pts[0]);
      if (kn.pts.length >= 3 && Math.hypot(f[0] - e.clientX, f[1] - e.clientY) < 18) kn.closed = true;
      else { const p = onKnifePlane(e); if (!p) return; kn.pts.push(p); }
    }
    hover = null;
    drawKnife();
    knifeChanged();
  }
  /** Плавная кривая через точки. */
  function spline(P: V3[], closed: boolean, per = 16): V3[] {
    const n = P.length;
    if (n < 3 && !closed) return P.slice();
    const at = (i: number): V3 => {
      if (closed) return P[((i % n) + n) % n];
      if (i < 0) return [2 * P[0][0] - P[1][0], 2 * P[0][1] - P[1][1], 2 * P[0][2] - P[1][2]];
      if (i > n - 1) return [2 * P[n - 1][0] - P[n - 2][0], 2 * P[n - 1][1] - P[n - 2][1], 2 * P[n - 1][2] - P[n - 2][2]];
      return P[i];
    };
    const tj = (a: V3, b: V3) => Math.sqrt(Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2])) || 1e-4;
    const out: V3[] = [];
    for (let i = 0; i < (closed ? n : n - 1); i++) {
      const p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
      const t1 = tj(p0, p1), t2 = t1 + tj(p1, p2), t3 = t2 + tj(p2, p3);
      for (let k = 0; k < per; k++) {
        const t = t1 + ((t2 - t1) * k) / per;
        const L = (a: V3, b: V3, ta: number, tb: number): V3 => a.map((x, j) => ((tb - t) * x + (t - ta) * b[j]) / (tb - ta)) as V3;
        const A1 = L(p0, p1, 0, t1), A2 = L(p1, p2, t1, t2), A3 = L(p2, p3, t2, t3);
        out.push(L(L(A1, A2, 0, t2), L(A2, A3, t1, t3), t1, t2));
      }
    }
    if (!closed) out.push(P[n - 1]);
    return out;
  }
  function knifeMsg(): Knife | null {
    if (!kn || (kn.pts.length < 2 && !kn.closed)) return null;
    return { id: kn.id, d: kn.d, pts: new Float32Array(spline(kn.pts, kn.closed).flat()), closed: kn.closed, flip: kn.flip };
  }
  /** Нож на экране: кривая, точки и полупрозрачная лента сквозь модель. */
  function drawKnife() {
    clearGroup(knifeG);
    ribbon = null;
    if (!kn || !all.has(kn.id)) return;
    const P = hover && !kn.closed ? [...kn.pts, hover] : kn.pts;
    const curve = spline(P, kn.closed);
    const { c, R } = centerOf(kn.id), d = kn.d;
    if (curve.length >= 2) {
      const ring = kn.closed ? [...curve, curve[0]] : curve;
      const lg = new THREE.BufferGeometry().setFromPoints(ring.map((p) => new THREE.Vector3(...p)));
      const ln = new THREE.Line(lg, new THREE.LineBasicMaterial({ color: 0xd32f2f, depthTest: false }));
      ln.renderOrder = 4;
      knifeG.add(ln);
      // лента: каждая точка кривой — отрезок вдоль взгляда через всю модель
      const pos: number[] = [], idx: number[] = [];
      ring.forEach((p, i) => {
        const k = dot([c[0] - p[0], c[1] - p[1], c[2] - p[2]], d);
        for (const s of [k - R - 5, k + R + 5]) pos.push(p[0] + d[0] * s, p[1] + d[1] * s, p[2] + d[2] * s);
        if (i) idx.push(2 * i - 2, 2 * i - 1, 2 * i, 2 * i - 1, 2 * i + 1, 2 * i);
      });
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setIndex(idx);
      ribbon = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xd32f2f, transparent: true, opacity: 0.13, side: THREE.DoubleSide, depthWrite: false }));
      knifeG.add(ribbon);
    }
    const dots = (list: V3[], color: number, size: number) => {
      const g = new THREE.BufferGeometry().setFromPoints(list.map((p) => new THREE.Vector3(...p)));
      const m = new THREE.Points(g, new THREE.PointsMaterial({ color, size, sizeAttenuation: false, depthTest: false }));
      m.renderOrder = 5;
      knifeG.add(m);
    };
    dots(kn.pts, 0xd32f2f, 9);
    // первая точка крупнее и белая, пока контур можно замкнуть
    if (!kn.closed && kn.pts.length >= 3) dots([kn.pts[0]], 0xffffff, 14);
  }
  function lnState() {
    if (mode !== 'line') return;
    if (!kn) { state(t.hint.line); return; }
    const n = String(kn.pts.length);
    state(kn.closed ? t.lnClosed.replace('{n}', n) : kn.pts.length < 2 ? t.lnOne : t.lnOpen.replace('{n}', n));
    $<HTMLButtonElement>('[data-lnflip]').hidden = false;
  }
  /** Превью ножа: сторона детали и штифты — после паузы. */
  function knifeChanged() {
    clearTimeout(knTimer);
    knOk = false;
    clearGroup(marks); clearGroup(planMarks);
    lnState();
    updateGo();
    const k = knifeMsg();
    if (!k || busy) { setPins(new Float32Array(0)); return; }
    knTimer = window.setTimeout(() => { if (!busy) send({ t: 'kplan', seq: ++seq, opts: { ...opts }, at: pinAt, knife: k }); }, 150);
  }
  function onKplan(r: Extract<Res, { t: 'kplan' }>) {
    if (r.seq !== seq || mode !== 'line' || !kn) return;
    clearGroup(marks);
    const b = all.get(r.id);
    if (b && r.tris.length) {
      const hl = new THREE.Mesh(subset(b, r.tris), mat('cut'));
      hl.userData.sharedMat = true;
      marks.add(hl);
    }
    showPlan(r.lines);
    lastPv = r.ms;
    warnings = r.notes;
    knOk = !r.notes.includes('noCut');
    setPins(r.pins);
    showWarn();
    updateGo();
  }
  $('[data-lnundo]').onclick = () => {
    if (mode === 'surf') {
      if (!sf || busy) return;
      if (sf.closed) sf.closed = false; else sf.pts.pop();
      if (!sf.pts.length) sf = null;
      sfLine = null; drawSurf(); surfChanged();
      return;
    }
    if (!kn || busy) return;
    if (kn.closed) kn.closed = false; else kn.pts.pop();
    if (!kn.pts.length) kn = null;
    drawKnife(); knifeChanged();
  };
  $('[data-lnclear]').onclick = () => {
    if (busy) return;
    if (mode === 'surf') { sf = null; sfLine = null; drawSurf(); surfChanged(); return; }
    kn = null; pinAt = null; drawKnife(); knifeChanged(); };
  $('[data-lnflip]').onclick = () => {
    if (mode === 'surf') { if (sf && !busy) { sf.flip = !sf.flip; surfChanged(); } return; }
    if (!kn || busy) return;
    kn.flip = !kn.flip; knifeChanged();
  };
  /** Точка ножа под курсором: номер ближайшей в пределах пальца/мыши. */
  function knPointAt(e: PointerEvent) {
    if (!kn) return -1;
    const lim = e.pointerType === 'mouse' ? 12 : 24;
    let best = -1, bd = lim;
    kn.pts.forEach((p, i) => { const s = toScreen(p), dd = Math.hypot(s[0] - e.clientX, s[1] - e.clientY); if (dd < bd) { bd = dd; best = i; } });
    return best;
  }
  // перетаскивание поставленной точки; ловим до холста, чтобы камера не крутилась
  let drag: { i: number; id: number; x: number; y: number; moved: boolean; raf: number } | null = null;
  stage.addEventListener('pointerdown', (e) => {
    if (mode !== 'line' || busy || e.button !== 0 || !e.isPrimary || !(e.target instanceof HTMLCanvasElement)) return;
    const i = knPointAt(e);
    if (i < 0) return;
    e.stopPropagation();
    drag = { i, id: e.pointerId, x: e.clientX, y: e.clientY, moved: false, raf: 0 };
    viewer.canvas.setPointerCapture(e.pointerId);
    hover = null;
  }, true);
  stage.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id || !kn) return;
    if (!drag.moved && Math.hypot(e.clientX - drag.x, e.clientY - drag.y) < 4) return;
    drag.moved = true;
    const p = onKnifePlane(e);
    if (!p) return;
    kn.pts[drag.i] = p;
    if (!drag.raf) drag.raf = requestAnimationFrame(() => { if (drag) drag.raf = 0; drawKnife(); });
  });
  const dragEnd = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    const d = drag;
    drag = null;
    if (d.raf) cancelAnimationFrame(d.raf);
    down = null;
    if (d.moved) { drawKnife(); knifeChanged(); }
    // без движения: клик по первой точке замыкает контур
    else if (e.type === 'pointerup' && d.i === 0 && kn && !kn.closed && kn.pts.length >= 3) addPoint(e);
  };
  stage.addEventListener('pointerup', dragEnd, true);
  stage.addEventListener('pointercancel', dragEnd, true);
  // резинка: линия тянется к курсору; над точкой — рука
  stage.addEventListener('pointermove', (e) => {
    if (mode !== 'line' || !kn || e.pointerType !== 'mouse' || e.buttons) return;
    viewer.canvas.style.cursor = knPointAt(e) >= 0 ? 'grab' : '';
    if (kn.closed) return;
    hover = onKnifePlane(e);
    if (!hoverRaf) hoverRaf = requestAnimationFrame(() => { hoverRaf = 0; drawKnife(); });
  });
  stage.addEventListener('pointerleave', () => { if (hover) { hover = null; drawKnife(); } });

  // ---------- «По поверхности» ----------
  // Точки ставятся на саму модель (треугольник — из луча), между ними модель можно крутить. Линию по коже,
  // сторону детали и плёнку среза считает поток; здесь — точки, ответ потока и прозрачность модели.
  type SurfUI = { id: number; pts: SPt[]; closed: boolean; flip: boolean };
  let sf: SurfUI | null = null;
  let sfOk = false, sfTimer = 0, ghost = false;
  /** Линия по коже от потока — до следующего ответа рисуется прежняя. */
  let sfLine: Float32Array | null = null;
  function surfHit(e: { clientX: number; clientY: number }) {
    const h = viewer.raycaster(e).intersectObjects(view.children, false)[0];
    const id = h?.object.userData.id as number | undefined;
    if (!h || id === undefined || h.faceIndex == null || all.get(id)!.kind === 'pin') return null;
    return { id, q: { p: v3(viewer.world.worldToLocal(h.point.clone())), f: h.faceIndex } as SPt };
  }
  function addSurfPoint(e: { clientX: number; clientY: number }) {
    if (busy || sf?.closed) return;
    if (sf && sf.pts.length >= 3) {
      const f = toScreen(sf.pts[0].p);
      if (Math.hypot(f[0] - e.clientX, f[1] - e.clientY) < 18) { sf.closed = true; drawSurf(); surfChanged(); return; }
    }
    const h = surfHit(e);
    if (!h) return;
    if (!sf) sf = { id: h.id, pts: [], closed: false, flip: false };
    else if (h.id !== sf.id) return;
    sf.pts.push(h.q);
    drawSurf();
    surfChanged();
  }
  function drawSurf() {
    clearGroup(surfG);
    if (!sf || !all.has(sf.id)) return;
    if (sfLine && sfLine.length >= 6) {
      const a = sf.closed ? Float32Array.from([...sfLine, sfLine[0], sfLine[1], sfLine[2]]) : sfLine;
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(a, 3));
      // линия прячется за моделью — видно, что она идёт по коже; в прозрачной модели видна целиком
      const ln = new THREE.Line(g, new THREE.LineBasicMaterial({ color: 0xd32f2f }));
      ln.renderOrder = 4;
      surfG.add(ln);
    }
    const dots = (list: V3[], color: number, size: number) => {
      const g = new THREE.BufferGeometry().setFromPoints(list.map((p) => new THREE.Vector3(...p)));
      const m = new THREE.Points(g, new THREE.PointsMaterial({ color, size, sizeAttenuation: false, depthTest: false }));
      m.renderOrder = 5;
      surfG.add(m);
    };
    dots(sf.pts.map((q) => q.p), 0xd32f2f, 9);
    if (!sf.closed && sf.pts.length >= 3) dots([sf.pts[0].p], 0xffffff, 14);
  }
  function sfState() {
    if (mode !== 'surf') return;
    if (!sf) { state(t.hint.surf); return; }
    const n = String(sf.pts.length);
    state(sf.closed ? t.sfClosed.replace('{n}', n) : sf.pts.length < 3 ? t.lnOne : t.sfOpen.replace('{n}', n));
  }
  function surfChanged() {
    clearTimeout(sfTimer);
    sfOk = false;
    clearGroup(marks); clearGroup(planMarks);
    warnings = []; showWarn();
    sfState();
    updateGo();
    if (!sf || busy) { sfLine = null; drawSurf(); return; }
    const q: Req = { t: 'splan', seq: ++seq, id: sf.id, pts: sf.pts.map((x) => ({ p: x.p, f: x.f })), closed: sf.closed, flip: sf.flip, opts: { ...opts } };
    if (sf.closed) status(t.counting);
    sfTimer = window.setTimeout(() => { if (!busy) send(q); }, 60);
  }
  function onSplan(r: Extract<Res, { t: 'splan' }>) {
    if (r.seq !== seq || mode !== 'surf' || !sf) return;
    status('');
    sfLine = r.line;
    clearGroup(marks); clearGroup(planMarks);
    const b = all.get(r.id);
    if (b && r.tris.length) {
      const hl = new THREE.Mesh(subset(b, r.tris), mat('cut'));
      hl.userData.sharedMat = true;
      marks.add(hl);
    }
    if (b && r.edge.length) {
      // поделенные линией треугольники — граница детали идёт ровно по линии
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(r.edge, 3));
      g.computeVertexNormals();
      const e = new THREE.Mesh(g, mat('cut'));
      e.userData.sharedMat = true;
      marks.add(e);
    }
    if (r.cap.length) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(r.cap, 3));
      const cap = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xffd54f, transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthWrite: false }));
      cap.renderOrder = 3;
      planMarks.add(cap);
    }
    if (r.hits.length) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(r.hits, 3));
      const m = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xff1744, size: 11, sizeAttenuation: false, depthTest: false }));
      m.renderOrder = 6;
      planMarks.add(m);
    }
    if (r.plug.length) {
      // пробка (то, что уйдёт в гнездо) — полупрозрачная, видна сквозь модель
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(r.plug, 3));
      const m = new THREE.Mesh(g, new THREE.MeshBasicMaterial({ color: 0xffb300, transparent: true, opacity: 0.35, side: THREE.DoubleSide, depthTest: false, depthWrite: false }));
      m.renderOrder = 4;
      planMarks.add(m);
    }
    if (r.lock.length) {
      // контур пробки и её торец
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(r.lock, 3));
      const l = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: 0xffd54f, depthTest: false }));
      l.renderOrder = 5;
      planMarks.add(l);
    }
    lastPv = r.ms;
    warnings = r.notes;
    showWarn();
    sfOk = sf.closed && (r.tris.length > 0 || r.edge.length > 0) && !r.notes.some((x) => x.startsWith('sf'));
    drawSurf();
    sfState();
    updateGo();
  }
  /** Прозрачная модель — видно линию и срез с обратной стороны. */
  function applyGhost() {
    const on = ghost && mode === 'surf';
    for (const k of ['body', 'piece', 'pin', 'cut']) {
      const m = mat(k);
      if (m.transparent === on) continue;
      m.transparent = on; m.opacity = on ? 0.35 : 1; m.depthWrite = !on; m.needsUpdate = true;
    }
    const b = $<HTMLButtonElement>('[data-ghost]');
    b.textContent = ghost ? t.solid : t.ghost;
    b.setAttribute('aria-pressed', String(ghost));
  }
  $('[data-ghost]').onclick = () => { ghost = !ghost; applyGhost(); };
  /** Точка под курсором: номер ближайшей в пределах пальца/мыши. */
  function sfPointAt(e: PointerEvent) {
    if (!sf) return -1;
    const lim = e.pointerType === 'mouse' ? 12 : 24;
    let best = -1, bd = lim;
    sf.pts.forEach((q, i) => { const s2 = toScreen(q.p), dd = Math.hypot(s2[0] - e.clientX, s2[1] - e.clientY); if (dd < bd) { bd = dd; best = i; } });
    return best;
  }
  // перетаскивание точки по модели; ловим до холста, чтобы камера не крутилась
  let sdrag: { i: number; id: number; x: number; y: number; moved: boolean; raf: number } | null = null;
  stage.addEventListener('pointerdown', (e) => {
    if (mode !== 'surf' || busy || e.button !== 0 || !e.isPrimary || !(e.target instanceof HTMLCanvasElement)) return;
    const i = sfPointAt(e);
    if (i < 0) return;
    e.stopPropagation();
    sdrag = { i, id: e.pointerId, x: e.clientX, y: e.clientY, moved: false, raf: 0 };
    viewer.canvas.setPointerCapture(e.pointerId);
  }, true);
  stage.addEventListener('pointermove', (e) => {
    if (!sdrag || e.pointerId !== sdrag.id || !sf) return;
    if (!sdrag.moved && Math.hypot(e.clientX - sdrag.x, e.clientY - sdrag.y) < 4) return;
    sdrag.moved = true;
    const h = surfHit(e);
    if (!h || h.id !== sf.id) return;
    sf.pts[sdrag.i] = h.q;
    sfLine = null;
    if (!sdrag.raf) sdrag.raf = requestAnimationFrame(() => { if (sdrag) sdrag.raf = 0; drawSurf(); });
  });
  const sdragEnd = (e: PointerEvent) => {
    if (!sdrag || e.pointerId !== sdrag.id) return;
    const d = sdrag;
    sdrag = null;
    if (d.raf) cancelAnimationFrame(d.raf);
    down = null;
    if (d.moved) { drawSurf(); surfChanged(); }
    // без движения: нажатие на первую точку замыкает контур
    else if (e.type === 'pointerup' && d.i === 0) addSurfPoint(e);
  };
  stage.addEventListener('pointerup', sdragEnd, true);
  stage.addEventListener('pointercancel', sdragEnd, true);
  stage.addEventListener('pointermove', (e) => {
    if (mode !== 'surf' || !sf || e.pointerType !== 'mouse' || e.buttons) return;
    viewer.canvas.style.cursor = sfPointAt(e) >= 0 ? 'grab' : '';
  });

  // ---------- штифты вручную ----------
  // Превью отвечает, где стоят штифты; их можно тянуть по срезу, убрать нажатием, добавить «+ Штифт».
  // С этого момента расстановка — ручная (pinAt), поток ставит каждый в ближайшее место, где он помещается.
  function setPins(a: Float32Array) {
    pinShow = [];
    for (let i = 0; i + 2 < a.length; i += 3) pinShow.push([a[i], a[i + 1], a[i + 2]]);
    drawPins();
    pinRow();
  }
  function drawPins() {
    clearGroup(pinG);
    if (!pinShow.length || opts.pins === 'none' || mode === 'bed') return;
    const g = new THREE.BufferGeometry().setFromPoints(pinShow.map((p) => new THREE.Vector3(...p)));
    const m = new THREE.Points(g, new THREE.PointsMaterial({ color: 0xffd54f, size: 14, sizeAttenuation: false, depthTest: false }));
    m.renderOrder = 6;
    pinG.add(m);
  }
  function pinRow() {
    const on = opts.pins !== 'none' && opts.joint !== 'tail' && mode !== 'bed' && mode !== 'surf' && (mode === 'line' ? !!kn && knOk : !!where);
    $('[data-pinrow]').hidden = !on;
    if (!on) return;
    $('[data-pinn]').textContent = t.pinN.replace('{n}', String(pinShow.length)) + (pinAt ? '' : t.pinAuto);
    $<HTMLButtonElement>('[data-pinreset]').hidden = !pinAt;
  }
  /** Точка на срезе под курсором: плоскость среза или лента ножа. */
  function cutPoint(e: { clientX: number; clientY: number }): V3 | null {
    if (mode === 'line') {
      if (!ribbon) return null;
      const h = viewer.raycaster(e).intersectObject(ribbon, false)[0];
      return h ? v3(viewer.world.worldToLocal(h.point.clone())) : null;
    }
    if (!where) return null;
    const r = localRay(e), o = v3(r.origin), d = v3(r.direction), n = where.plane.n, den = dot(d, n);
    if (Math.abs(den) < 1e-4) return null;
    const k = (where.plane.s - dot(o, n)) / den;
    return k > 0 ? [o[0] + d[0] * k, o[1] + d[1] * k, o[2] + d[2] * k] : null;
  }
  const pinsChanged = () => { drawPins(); pinRow(); if (mode === 'line') knifeChanged(); else refreshPlan(); };
  function placePin(e: PointerEvent) {
    pinArm = false;
    const p = cutPoint(e);
    if (mode === 'line') lnState(); else state(stateText());
    if (!p) return;
    pinAt = [...pinShow, p];
    pinShow = pinAt.slice();
    pinsChanged();
  }
  /** Что написано в шаге ① сейчас (после подсказки «нажмите на срез» — вернуть). */
  const stateText = () => mode === 'part' ? (sel ? (sel.found ? t.found : t.notFound) : t.hint.part) : mode === 'brush' ? (sel ? t.painted : t.hint.brush) : t.hint.plane;
  $('[data-pinadd]').onclick = () => {
    if (busy) return;
    pinArm = true;
    state(t.pinPlace);
    stage.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };
  $('[data-pinreset]').onclick = () => { if (!busy) { pinAt = null; pinsChanged(); } };
  function pinIndexAt(e: PointerEvent) {
    const lim = e.pointerType === 'mouse' ? 12 : 24;
    let best = -1, bd = lim;
    pinShow.forEach((p, i) => { const s = toScreen(p), dd = Math.hypot(s[0] - e.clientX, s[1] - e.clientY); if (dd < bd) { bd = dd; best = i; } });
    return best;
  }
  let pdrag: { i: number; id: number; x: number; y: number; moved: boolean; raf: number } | null = null;
  stage.addEventListener('pointerdown', (e) => {
    if (drag || pinArm || busy || e.button !== 0 || !e.isPrimary || !(e.target instanceof HTMLCanvasElement) || mode === 'bed' || !pinG.children.length) return;
    if (mode === 'brush' && painting) return;
    const i = pinIndexAt(e);
    if (i < 0) return;
    e.stopPropagation();
    pdrag = { i, id: e.pointerId, x: e.clientX, y: e.clientY, moved: false, raf: 0 };
    viewer.canvas.setPointerCapture(e.pointerId);
    hover = null;
  }, true);
  stage.addEventListener('pointermove', (e) => {
    if (!pdrag || e.pointerId !== pdrag.id) return;
    if (!pdrag.moved && Math.hypot(e.clientX - pdrag.x, e.clientY - pdrag.y) < 4) return;
    pdrag.moved = true;
    const p = cutPoint(e);
    if (!p) return;
    pinShow[pdrag.i] = p;
    if (!pdrag.raf) pdrag.raf = requestAnimationFrame(() => { if (pdrag) pdrag.raf = 0; drawPins(); });
  });
  const pdragEnd = (e: PointerEvent) => {
    if (!pdrag || e.pointerId !== pdrag.id) return;
    const d = pdrag;
    pdrag = null;
    if (d.raf) cancelAnimationFrame(d.raf);
    down = null;
    if (e.type === 'pointercancel') return;
    // без движения — убрать штифт
    if (!d.moved) pinShow.splice(d.i, 1);
    pinAt = pinShow.slice();
    pinsChanged();
  };
  stage.addEventListener('pointerup', pdragEnd, true);
  stage.addEventListener('pointercancel', pdragEnd, true);
  stage.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' || e.buttons || !pinG.children.length || (mode === 'line' && kn && knPointAt(e) >= 0)) return;
    if (pinIndexAt(e) >= 0) viewer.canvas.style.cursor = 'grab';
    else if (mode !== 'line') viewer.canvas.style.cursor = '';
  });

  // ---------- файл ----------
  async function open(f: File) {
    if (busy) return;
    busy = true;
    lastFile = f;
    fileName = f.name.replace(/\.[^.]+$/, '') || 'model';
    const drop = $('[data-drop]');
    drop.classList.add('got');
    drop.querySelector('.btn')!.textContent = t.again;
    $('[data-fname]').textContent = f.name;
    sel = null; where = null; grid = null;
    $('[data-adj]').hidden = true;
    clearMarks();
    clearPaint();
    kn = null; knOk = false; clearGroup(knifeG);
    sf = null; sfOk = false; sfLine = null; clearGroup(surfG);
    msg('');
    status(t.loading);
    try {
      const buf = await f.arrayBuffer();
      // новый файл — новый поток: память WASM с чистого листа
      worker?.terminate(); worker = null; onRes = null;
      const r = await ask({ t: 'open', buf, file: f.name, mf: mfURL }, 'bodies', [buf]);
      for (const g of geoms.values()) g.geo.dispose();
      geoms.clear(); all.clear(); hist.length = 0;
      for (const b of r.list) all.set(b.id, b);
      live = r.list.map((b) => b.id);
      shift = r.shift; plate = r.plate;
      $('[data-work]').hidden = false;
      draw();
      frameAll();
      busy = false;
      status('');
      planeInit();
      setMode(mode);
    } catch (e) {
      console.error(e);
      msg(errText(e), true);
      $('[data-work]').hidden = false;
      busy = false;
      status('');
    }
  }
  const sampleBtn = root.querySelector<HTMLButtonElement>('[data-sample]');
  const take = (f?: File | null) => { if (f) { if (sampleBtn) sampleBtn.hidden = true; void open(f); } };
  // файл-пример из админки: скачать и открыть как выбранный файл
  if (sample && sampleBtn) {
    const openSample = async () => {
      sampleBtn.disabled = true;
      try {
        const r = await fetch(sample.url);
        if (!r.ok) throw new Error(String(r.status));
        take(new File([await r.blob()], sample.name));
        (window as { gen3dHit?: (e: string) => void }).gen3dHit?.('sample_open');
      } catch (e) { console.warn('sample', e); msg(t.sampleErr, true); }
      sampleBtn.disabled = false;
    };
    sampleBtn.onclick = () => void openSample();
    if (sample.auto) void openSample();
  }
  const input = $<HTMLInputElement>('[data-file]');
  input.onchange = () => { const f = input.files?.[0]; input.value = ''; take(f); };
  const drop = $('[data-drop]');
  for (const ev of ['dragenter', 'dragover'] as const) drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('over'); });
  for (const ev of ['dragleave', 'drop'] as const) drop.addEventListener(ev, () => drop.classList.remove('over'));
  drop.addEventListener('drop', (e) => { e.preventDefault(); take(e.dataTransfer?.files[0]); });
  stage.addEventListener('dragover', (e) => e.preventDefault());
  stage.addEventListener('drop', (e) => { e.preventDefault(); take(e.dataTransfer?.files[0]); });

  // ---------- разрез ----------
  /** Убрать из памяти тела, которых нет ни на столе, ни в истории. */
  function prune() {
    const keep = new Set([...live, ...hist.flat()]);
    for (const id of [...all.keys()]) if (!keep.has(id)) { all.delete(id); geoms.get(id)?.geo.dispose(); geoms.delete(id); }
  }
  /** После разреза или отмены — всё заново: выбор снят, превью текущего режима. */
  function afterChange() {
    sel = null; where = null; grid = null;
    clearPaint();
    kn = null; knOk = false; clearGroup(knifeG);
    sf = null; sfOk = false; sfLine = null; clearGroup(surfG);
    $('[data-adj]').hidden = true;
    draw();
    frameAll();
    if (mode === 'plane') planeInit();
    setMode(mode);
  }
  async function run() {
    if (busy) return;
    const w = where, g = grid, f = sf;
    if (mode === 'bed' ? !g?.cuts.length : mode === 'line' ? !kn || !knOk : mode === 'surf' ? !f || !sfOk : !w) return;
    const job = work(), est = K * job;
    busy = true;
    clearTimeout(sideTimer); clearTimeout(planTimer); clearTimeout(sfTimer); seq++;
    const bar = $('[data-bar]'), fill = bar.querySelector('i')!;
    goBtn.hidden = true;
    bar.hidden = false; fill.style.width = '0';
    msg('');
    const t0 = performance.now();
    // полоска идёт по оценке, пока поток не скажет точнее
    const tick = () => {
      const k = Math.min(0.95, (performance.now() - t0) / 1000 / Math.max(est, 0.5));
      if (parseFloat(fill.style.width || '0') < k * 100) fill.style.width = `${k * 100}%`;
      status(`${t.cutting}… ${secs((performance.now() - t0) / 1000)} / ≈ ${secs(est)}`);
    };
    tick();
    const timer = window.setInterval(tick, 250);
    try {
      const q: Req = mode === 'bed' ? { t: 'gridcut', cuts: g!.cuts, opts: { ...opts } }
        : mode === 'line' ? { t: 'knife', opts: { ...opts }, at: pinAt, knife: knifeMsg()! }
        : mode === 'surf' ? { t: 'scut', opts: { ...opts }, id: f!.id, pts: f!.pts.map((x) => ({ p: x.p, f: x.f })), flip: f!.flip }
        : { t: 'cut', opts: { ...opts }, at: pinAt, ...w! };
      const r = await ask(q, 'cut', [], (k) => { if (parseFloat(fill.style.width || '0') < k * 100) fill.style.width = `${Math.round(k * 100)}%`; });
      hist.push(live);
      while (hist.length > 3) hist.shift();
      for (const b of r.add) all.set(b.id, b);
      live = r.order;
      prune();
      const sec = (performance.now() - t0) / 1000;
      // скорость этого компьютера — для следующих оценок
      if (job > 1e4) { K = (K + sec / job) / 2; try { localStorage.setItem('gen3d:cutter:k', String(K)); } catch { /* нет хранилища */ } }
      busy = false;
      clearInterval(timer);
      bar.hidden = true;
      status('');
      afterChange();
      msg([t.done.replace('{t}', secs(sec)), ...r.notes.map((n) => t.warn[n as keyof typeof t.warn] ?? '')].join(' '));
    } catch (e) {
      console.error(e);
      busy = false;
      clearInterval(timer);
      bar.hidden = true;
      status('');
      msg(errText(e), true);
      if ((e as Error).message === 'MEMORY') { worker?.terminate(); worker = null; onRes = null; }
      updateGo();
    }
  }
  goBtn.onclick = () => void run();
  $('[data-undo]').onclick = async () => {
    if (busy || !hist.length) return;
    busy = true;
    try {
      const r = await ask({ t: 'undo' }, 'undo');
      if (r.ok) { live = hist.pop()!; prune(); }
    } catch (e) { console.error(e); }
    busy = false;
    msg('');
    afterChange();
  };

  // ---------- настройки ----------
  function syncTiles() {
    const jt = opts.joint;
    params.querySelectorAll<HTMLButtonElement>('[data-set]').forEach((b) => {
      const [k, v] = b.dataset.set!.split(':');
      const on = k === 'axis' ? String(pl.axis) === v : k === 'brush' ? (erase ? 'erase' : 'paint') === v : k === 'joint' ? jt === v : opts[k as 'pins' | 'place'] === v;
      b.setAttribute('aria-pressed', String(on));
    });
    params.querySelector<HTMLElement>('[data-set="joint:tail"]')!.hidden = mode !== 'plane';
    $('[data-joints]').style.setProperty('--n', mode === 'plane' ? '3' : '2');
    // у «По поверхности» пока только замок: без штифтов и без скоса
    $('[data-pins-g]').hidden = jt === 'tail' || mode === 'surf';
    params.querySelector<HTMLElement>('[data-f="area"]')!.hidden = mode === 'surf';
    // у среза по поверхности пробка идёт от самого края: бортика нет, вместо него — угол стенок
    params.querySelector<HTMLElement>('[data-f="rim"]')!.hidden = mode === 'surf';
    params.querySelector<HTMLElement>('[data-f="angle"]')!.hidden = mode !== 'surf';
    $('[data-jhint]').textContent = mode === 'line' && jt === 'lock' ? t.lnLock : mode === 'surf' && jt === 'lock' ? t.sfLock : t.jHint[jt];
    pinRow();
    params.querySelector<HTMLElement>('[data-f="lock"]')!.hidden = jt !== 'lock';
    params.querySelector<HTMLElement>('[data-f="tail"]')!.hidden = jt !== 'tail';
    params.querySelector<HTMLElement>('[data-f="pins"]')!.hidden = opts.pins === 'none' || jt === 'tail' || mode === 'surf';
    params.querySelector<HTMLElement>('[data-f="tol"]')!.hidden = jt === 'none' && (opts.pins === 'none' || mode === 'surf');
  }
  params.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-set]');
    if (!b || busy) return;
    const [k, v] = b.dataset.set!.split(':');
    if (k === 'brush') { erase = v === 'erase'; setTool(true); syncTiles(); return; }
    if (k === 'axis') { pinAt = null; pl.axis = +v as 0 | 1 | 2; pl.a = pl.b = 0; pl.pos = planeBox().size / 2; syncPlane(); refresh(true); return; }
    if (k === 'joint') opts.joint = v as Opts['joint'];
    else if (k === 'pins') opts.pins = v as Opts['pins'];
    else opts.place = v as Opts['place'];
    syncTiles();
    save();
    if (k !== 'place') refreshPlan();
  });
  params.addEventListener('input', (e) => {
    const i = e.target as HTMLInputElement;
    if (i.dataset.o && i.type === 'range') apply(i.dataset.o, +i.value);
  });
  params.addEventListener('change', (e) => {
    const i = e.target as HTMLInputElement;
    if (i.dataset.o && i.type === 'number') apply(i.dataset.o, +i.value);
  });
  function apply(k: string, raw: number) {
    const lim: readonly number[] | undefined = k === 'pos' ? [0, planeBox().size, 0.5] : k === 'brushR' ? BR : k === 'pa' || k === 'pb' ? ADJ.a
      : (LIM as Record<string, readonly number[]>)[k] ?? (ADJ as Record<string, readonly number[]>)[k];
    if (!lim) return;
    const v = Math.min(lim[1], Math.max(lim[0], isFinite(raw) ? Math.round(raw / lim[2]) * lim[2] : 0));
    const r = Math.round(v * 100) / 100;
    params.querySelectorAll<HTMLInputElement>(`[data-o="${k}"]`).forEach((x) => { if (+x.value !== r) x.value = String(r); });
    if (k === 'off' || k === 'a' || k === 'b') { if (sel) { sel[k] = r; refresh(); } return; }
    if (k === 'pos') { pl.pos = r; refresh(); return; }
    if (k === 'brushR') { brushR = r; save(); return; }
    if (k === 'pa') { pl.a = r; refresh(); return; }
    if (k === 'pb') { pl.b = r; refresh(); return; }
    opts[k as keyof typeof LIM] = r;
    save();
    refreshPlan();
  }
  $('[data-reset]').onclick = () => { if (sel) { sel.off = sel.a = sel.b = 0; for (const k of ['off', 'a', 'b']) setAdj(k, 0); refresh(true); } };
  $('[data-clear]').onclick = () => select(null);
  $('[data-flip]').onclick = () => { if (!busy) { pl.flip = !pl.flip; refresh(true); } };
  $('[data-preset]').onclick = () => { if (!busy) { pl.a = pl.b = 0; pl.flip = false; pl.pos = planeBox().size / 2; syncPlane(); refresh(true); } };

  const bp = bedPicker(params, lang, (b) => {
    viewer.setBed(b.x, b.y);
    bed = { x: b.x, y: b.y, z: b.z };
    if (mode === 'bed' && live.length) { $('[data-bed-is]').textContent = t.bedIs.replace('{s}', `${bed.x} × ${bed.y} × ${bed.z}`); refresh(true); }
  });
  bed = { x: bp.x, y: bp.y, z: bp.z };
  viewer.setBed(bp.x, bp.y);
  viewer.frame(120, 120, 40);
  stage.querySelectorAll<HTMLButtonElement>('[data-mode]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.mode === mode)));
  syncTiles();
  setReady();
  save();

  // ---------- скачивание ----------
  function model(): Model {
    if (!ready()) throw new Error(t.dlWait);
    const parts: Part[] = [];
    const objects: Placed[] = [];
    for (const id of live) {
      const b = all.get(id)!;
      parts.push({ name: b.name, role: b.kind === 'body' ? 'base' : 'detail', geometry: geomOf(b).geo });
      objects.push({ name: b.name, parts: [parts.length - 1], at: [[0, 0, 0]] });
    }
    return { name: `${fileName}-cut`, parts, objects };
  }
  /** Исходник из Bambu Studio / Orca — 3MF собирается из него: принтер, пластики и профиль сохраняются. */
  async function withBambu(m: Model): Promise<Model> {
    const f = lastFile;
    if (!f || !/\.3mf$/i.test(f.name)) return m;
    const src = new Uint8Array(await f.arrayBuffer());
    if (!isBambu(src)) return m;
    const objects: BObject[] = (m.objects ?? []).map((o) => ({
      name: o.name,
      // в просмотре модель сдвинута к центру стола; в файле — на свои места
      at: [[-shift[0], -shift[1], -shift[2]]],
      parts: o.parts.map((i) => {
        const g = m.parts[i].geometry;
        return { name: m.parts[i].name, pos: g.getAttribute('position').array as Float32Array, tri: Uint32Array.from(g.index!.array as ArrayLike<number>), ext: 1 };
      }),
    }));
    return { ...m, file: { '3MF': () => toBambu3MF(src, objects, plate) } };
  }
  return { model: () => withBambu(model()) };
}
